import { useRef, useState } from "react";
import { Typography, Paper, Button, Divider } from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { useSnackbar } from "notistack";

import DateUtils from "utils/DateUtils";
import StimFormReportDocx from "views/hospital/patient/assessment/StimFormReportDocx";
import SnackbarConstants from "utils/constants/Snackbar.js";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import Stack from "@mui/material/Stack";
import html2pdf from "html2pdf.js";
import ApiBaseService from "services/ApiBaseService";
import { useParams, useOutletContext } from "react-router-dom";




const StimFormReport = ({ reportData }) => {
  const { patientId } = useParams();
  const [hospitalId] = useOutletContext();

  const targetRef = useRef();
  const { enqueueSnackbar } = useSnackbar();
  const [sending, setSending] = useState(false);

  const handleDownload = () => {
    enqueueSnackbar("Report download started. This may take a few seconds...", SnackbarConstants.success);
    StimFormReportDocx(reportData);
  };
  const getReportFileName = () => {
    const firstName = reportData?.firstName || "Patient";
    const lastName = reportData?.lastName || "";
    return `Home_Program_Report_${firstName}_${lastName}`;
  };

  const handleDownloadPdf = async () => {
    try {
      enqueueSnackbar(
        "Generating PDF. Please wait...",
        SnackbarConstants.info
      );

      const pdfBlob = await generatePdfBlob();

      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${getReportFileName()}.pdf`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar(
        "PDF downloaded successfully",
        SnackbarConstants.success
      );
    } catch (error) {
      enqueueSnackbar(
        "Failed to generate PDF",
        SnackbarConstants.error
      );
    }
  };

  const generatePdfBlob = async () => {
    const element = targetRef.current;

    return await html2pdf()
      .set({
        margin: 10,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: {
          scale: 1.3,
          useCORS: true,
          scrollY: 0
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait"
        },
        pagebreak: {
          mode: ["avoid-all", "css"]
        }
      })
      .from(element)
      .outputPdf("blob");
  };

  const handleEmailPatient = async () => {
    try {
      setSending(true);
      enqueueSnackbar("Generating report and sending email...", SnackbarConstants.info);

      const pdfBlob = await generatePdfBlob();
      const formData = new FormData();
      formData.append("file", pdfBlob, "stim-report.pdf");
      console.log({
        hospitalId,
        patientId: patientId,
        assessmentFormId: reportData.id,
      });


      await ApiBaseService.request(
        `/hospitals/${hospitalId}/patients/${patientId}/assessment-forms/${reportData.id}/email-report`,
        "POST",
        formData
      );

      enqueueSnackbar("Report emailed successfully", SnackbarConstants.success);
    } catch (err) {
      enqueueSnackbar("Failed to email report", SnackbarConstants.error);
    } finally {
      setSending(false);
    }


  };




  return (
    <Paper elevation={0} className="py-2 px-2">
      <div
        className="d-flex justify-content-between p-3 align-items-center"
        style={{
          position: "sticky",
          top: 0,
          backgroundColor: "#e6f0ff",
          borderRadius: 5,
          zIndex: 1,
        }}
      >
        <Typography variant="h2">
          {reportData.ageGroup} Report Generated
        </Typography>
        <div>
          <Stack direction="row" spacing={2}>
            <Button
              type="button"
              startIcon={<PictureAsPdfRoundedIcon />}
              onClick={handleDownloadPdf}
              variant="outlined"
            >
              Download PDF
            </Button>

            <Button
              type="button"
              startIcon={<DownloadRoundedIcon />}
              onClick={handleDownload}
              variant="outlined"
            >
              Download Word
            </Button>
            <Button
              startIcon={<MailOutlineRoundedIcon />}
              variant="contained"
              onClick={handleEmailPatient}
              disabled={sending}
            >
              Email Patient
            </Button>

          </Stack>
        </div>
      </div>
      <Report targetRef={targetRef} reportData={reportData} />
    </Paper>
  );
};

const Report = ({ targetRef, reportData }) => {
  const firstName = reportData.firstName || "Patient";
  const ageGroup = reportData.ageGroup;
  const program = reportData.program;

  const genderSubjectPronoun = reportData.gender === "Male" ? "he" : "she";
  const genderAdjective = reportData.gender === "Male" ? "his" : "her";
  const genderObjectAdjective = reportData.gender === "Male" ? "him" : "her";

  return (
    <Paper ref={targetRef} className="p-5" elevation={2}>
      <header className="text-center">
        <img
          src="../../assets/melillo_method_image.png"
          width={100}
          alt="Dr. Robert Melillo"
        />
        <Typography variant="h1">
          {reportData.program?.toUpperCase()} HEMISPHERE HOME PROGRAM
        </Typography>
        <Typography variant="h3" className="mt-3">
          Patient :{" "}
          <u>
            {reportData.firstName} {reportData.lastName}
          </u>
        </Typography>
        <Typography variant="h3" className="mt-1">
          {reportData.isReevaluation ? "Re-Evaluation" : "Initial Evaluation"} :{" "}
          <u>{DateUtils.formatDateTimeToDate(reportData.evaluationDate)}</u>
        </Typography>
        <Typography variant="h3" className="mt-1">
          Goal Level:{' '}
          <u>
            {(() => {
              if (reportData.age <= 2) return 1;
              if (reportData.age >= 11) return 10;
              return reportData.age - 1;
            })()}
          </u>
        </Typography>
      </header>
      <main className="p-5">
        <section className="py-3">
          <Typography variant="h3">Melillo Method 10 Level System</Typography>
          <Typography variant="body1" className="mt-2">The Melillo Method program is designed to be dynamic and change as you progress with each part of the home program. Each task has 10 levels which are based on the patient’s age. The overall goal level listed at the top of this program is the ultimate goal for each task in this home program. The evaluation determines the starting level and each task you will see in this program has its own set of criteria that must be met before moving up to the next level. In order to progress to the next level, all criteria for the task must be met for 5 sessions in a row. Once this is achieved, you are encouraged to request the next level and a video will be sent to you with a more advanced version of that task with new criteria. Ideally, you or your child should be able to advance to a new level every 6-8 weeks with the overall goal being to reach their goal level in each task. This 10 level system ensures the program is individualized to each person and is designed to change as you progress through the program.
          </Typography>
        </section>
        {/* Primitive Reflexes */}
        {reportData.sections.primitiveReflexes && (
          <>
            <section className="py-3">
              <Typography variant="h3">
                Primitive Reflexes Exercises/Stimulation
              </Typography>
              {reportData.isReevaluation ? (
                <Typography variant="body1" className="mt-2">
                  It was great to see {reportData.firstName} for a reevaluation
                  {reportData.isVirtual ? "" : " in the office"} to re-assess the progress. Based upon the re-evaluation, {reportData.firstName} does still have primitive reflexes present at this time. We have updated the home program and the family will continue to work on these daily.
                </Typography>
              ) : reportData.isVirtual ? (
                <Typography variant="body1" className="mt-2">
                  Based upon the paperwork completed and my consultation call, I
                  confirmed that {reportData.firstName} has a{" "}
                  {reportData.program} hemisphere deficit and most of{" "}
                  {genderAdjective} primitive reflexes are still present.
                  Research has shown that retained primitive reflexes will lead
                  to imbalances in the brain so these will be important to focus
                  on as part of the daily home program.{" "}
                  {reportData.ageGroup === "Child" ? "The family" : "You"} will
                  do these stimulation exercises{" "}
                  {reportData.ageGroup === "Child" &&
                    "with " + reportData.firstName}{" "}
                  for 1 - 2 times per day as part of the home program.
                </Typography>
              ) : (
                <Typography variant="body1" className="mt-2">
                  Based upon the paperwork completed, the physical examination
                  and testing, we confirmed that {reportData.firstName} has a{" "}
                  {reportData.program} hemisphere deficit, and most all
                  primitive reflexes are still present. Research has clearly
                  shown that retained Primitive Reflexes will lead to imbalances
                  in the brain, so it is important{" "}
                  {reportData.ageGroup === "Child" && "for the family"} to work
                  on these as part of {reportData.firstName}’s home program.
                  These are recommended to be done twice daily
                  {reportData.ageGroup === "Child" &&
                    " with " + reportData.firstName}
                  .<br></br>
                </Typography>
              )}
              <div className="mt-4">
                {Object.entries(reportData.sections.primitiveReflexes).map(
                  ([reflex, value], index) => (
                    <div className="mt-2" key={`pm-${index}`}>
                      <PrimitiveReflex
                        reflex={reflex}
                        level={value}
                        genderAdjective={genderAdjective}
                      />
                    </div>
                  )
                )}
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* Core Stability */}
        <section className="py-3">
          <Typography variant="h3">Core Stability Exercises</Typography>
          {reportData.isReevaluation ? <Typography variant="body1" className="mt-2">
            To continue to work on building core strength and increasing stability around the spine, {reportData.firstName} will continue with these updated core exercises as part of the daily home program.
          </Typography> :
            <Typography variant="body1" className="mt-2">
              These are important for {reportData.firstName} to create spinal
              stability, and build core strength. The spine carries a lot of
              information to the brain, so this is important for many reasons.
              These will be part of {reportData.firstName}’s daily home program
              also.
            </Typography>}
          {reportData.sections.coreStability.prone && (
            <div className="mt-2">

              <CSProne level={reportData.sections.coreStability.prone} />
            </div>
          )}
          {reportData.sections.coreStability.lateral && (
            <div className="mt-2">

              <CSLateral level={reportData.sections.coreStability.lateral} />
            </div>
          )}
          {reportData.sections.coreStability.supine && (
            <div className="mt-2">

              <CSSupine level={reportData.sections.coreStability.supine} />
            </div>
          )}
          {reportData.sections.coreStability.curlUps && (
            <div className="mt-2">

              <CSCurlUps
                level={reportData.sections.coreStability.curlUps}
              />
            </div>
          )}
          {reportData.sections.coreStability.pushUps && (
            <div className="mt-2">

              <CSPushUps
                level={reportData.sections.coreStability.pushUps}
              />
            </div>
          )}
          {reportData.sections.coreStability.pullUps && (
            <div className="mt-2">
              <CSPullUps level={reportData.sections.coreStability.pullUps} />
            </div>
          )}
        </section>

        {/* Balance Exercises */}
        {reportData.sections.balanceExercise && reportData.sections.balanceExercise !== "0" && (
          <>
            <section className="py-3">
              <Typography variant="h3">Balance Exercises</Typography>
              <div className="mt-2">
                <BalanceExercise
                  level={reportData.sections.balanceExercise}
                  genderAdjective={genderAdjective}
                />
              </div>
            </section>
            <Divider />
          </>
        )}


        {/* Galvanic */}
        {reportData.sections.galvanic.length > 0 && (
          <>
            <section className="py-3">
              <Typography variant="h3">Galvanic</Typography>
              <div className="mt-2">
                <Typography variant="body1">
                  We will also be recommending the <a href="https://dr-robert-melillo.myshopify.com/products/micro-z-ii-mini-unit-for-home-therapy-galvanic-stimulation">Galvanic Device</a> to use as part of the home program.  This will be used at home while doing the home program including the vestibular activities, balance and Metronome as instructed. <a href="https://www.dropbox.com/scl/fi/5uir4vyqqay7fdgfftxx0/Melillo-Galvanic-Stimulator-Instructions-V2.mp4?rlkey=rb454jhozex52j4vwiy674vbn&st=9k0q97oe&dl=0">The instruction video</a> for the unit and written instructions can be used for reference also.
                </Typography>
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* Shokz */}
        {reportData.sections.shokz.length > 0 && (
          <>
            <section className="py-3">
              <Typography variant="h3">Bone Conducting Headphones (Shokz)</Typography>
              <div className="mt-2">
                <Typography variant="body1">
                  I have also recommended the <a href="https://a.co/d/8Q3Kv88">Shokz Bone Conducting Headphones</a> to use as part of the home program. These will be used as part of the home program while spinning or doing vestibular activities, balance and metronome activities with the <b>TONER PACER PRO app</b> on a tablet or phone. These can be purchased in our office or on Amazon.
                  <br />
                  <a href="https://www.dropbox.com/scl/fi/iq0nc822mb7wcx6hi7qq2/Melillo-Bone-Conduction-Instruction-V2.mp4?rlkey=7lzfhopqmz16f0o8sv93ixqhm&st=jq6804jx&dl=0">Bone Conducting Headphones Instruction Video</a>
                </Typography>
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* Vestibular Exercises */}
        {(reportData.sections.vestibular?.spinning?.length > 0 || reportData.sections.vestibular.level) && (
          <>
            <section className="py-3">
              <Typography variant="h3">Vestibular Exercises</Typography>
              <div className="mt-2">
                <VestibularSpinning
                  level={reportData.sections.vestibular.level
                    ? reportData.sections.vestibular.level
                    : reportData.sections.vestibular.spinning.length > 0
                      ? "1"
                      : "0"}
                />
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* Hemispheric Sensory Stimulus */}
        <section className="py-3">
          <Typography variant="h3">Hemispheric Sensory Stimulus</Typography>
          <Typography variant="body1" className="mt-2">
            Recommend that {reportData.firstName} receive regular chiropractic
            manipulations also as part of the therapy program by a well-trained
            professional. These will be done to the{" "}
            {reportData.program === "left" ? "right" : "left"} side only of the
            cervical spine and the right and left side of the thoracic and
            lumbar spine.
          </Typography>
        </section>
        <Divider />

        {/* Light Stimulation */}
        {reportData.sections.eyeLights.length > 0 && (
          <>
            <section className="py-3">
              <Typography variant="h3">Light Stimulation</Typography>
              <div className="mt-4">
                {reportData.sections.eyeLights.map((eyelight, index) => (
                  <LSEyelights
                    eyelight={eyelight}
                    key={`eyelight-${index}`}
                  />
                ))}
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* Brain Balance Music */}
        {reportData.sections.brainBalanceMusic.length > 0 && (
          <>
            <section className="py-3">
              <Typography variant="h3">Sound Therapy</Typography>
              <div className="mt-2">
                {reportData.sections.brainBalanceMusic.map((value, index) => (
                  <div className="mt-2">
                    <BrainBalanceMusic
                      value={value}
                      key={`sound-${index}`}
                    />
                  </div>
                ))}
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* Interactive Metronome */}
        {reportData.sections.interactiveMetronome && (
          <>
            <section className="py-3">
              <Typography variant="h3">Interactive Metronome</Typography>
              <div className="mt-2">
                <InteractiveMetronome
                  level={reportData.sections.interactiveMetronome}
                />
                <a href="https://www.interactivemetronome.com/im-home-for-clients" className="d-flex justify-content-center mt-1" target="_blank">
                  Learn more about Interactive Metronome
                </a>
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* Occulomotor */}
        {reportData.sections.occulomotor && (
          <>
            <section className="py-3">
              <Typography variant="h3">Occulomotor</Typography>
              {["1", "2", "3"].includes(String(reportData.sections.occulomotor)) && (
                <Typography variant="body1" className="mt-2">
                  We recommend downloading the <b>OKN App</b> (Google or Apple) on a phone for these activities. <a href="https://www.dropbox.com/scl/fi/nj7u0n9zxpp9e9xfgii12/Melillo-OKN-Drum-Instructions.mp4?rlkey=p4u1vlxb5aqmxvg3ewgm6mqrm&st=dlkg1h0s&dl=0">OKN App Set Up Instructions</a>
                </Typography>
              )}
              <div className="mt-2">
                <Occulomotor
                  level={reportData.sections.occulomotor}
                />
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* Olfactory */}
        {reportData.sections.olfactory && (
          <>
            <section className="py-3">
              <Typography variant="h3">Olfactory</Typography>
              <div className="mt-2">
                <Olfactory
                  level={reportData.sections.olfactory}
                />
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* AuditoryProcessing */}
        {reportData.sections.auditoryProcessing && (
          <>
            <section className="py-3">
              <Typography variant="h3">Auditory Processing</Typography>
              <div className="mt-2">
                <AuditoryProcessing
                  level={reportData.sections.auditoryProcessing}
                />
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* MetroTimer */}
        {reportData.sections.metroTimer && (
          <>
            <section className="py-3">
              <Typography variant="h3">MetroTimer</Typography>
              <Typography variant="body1" className="mt-2">
                <b>MetroTimer App</b> - Recommend downloading and using the Metrotimer App as part of the daily home program. <a href="https://www.dropbox.com/scl/fi/1w3angdyqrxuekbmcy8qg/Metro-Timer-Intro.mp4?rlkey=7gypwoxi7txqktthqxe7f5upk&st=oanwmlgi&dl=0">MetroTimer App Instructions to set up</a>
              </Typography>
              <div className="mt-2">
                <MetroTimer
                  level={reportData.sections.metroTimer}
                />
              </div>
            </section>
            <Divider />
          </>
        )}


        {/* TENs Unit */}
        <section className="py-3">
          <Typography variant="h3">Tactile - TENS Unit</Typography>
          <Typography variant="body1" className="mt-4">
            The TENS unit is used to activate nerve fibers for sensory stimulation to the body.
            {reportData.isReevaluation
              ? (<>
                The TENS unit is used to activate nerve fibers for sensory stimulation to the body. {firstName} is recommended to continue to use the TENS unit for a minimum of 20 minutes twice daily as part of the home program.  The goal will be to increase the time <b>every 2 weeks by 10 minutes to the maximum time of 60 minutes twice daily</b>, combined with using the Eyelights and Brain Driver at the same time.The pad will continue to be placed on the {program === 'right' ? 'left' : 'right'} side of the upper back between the spine and shoulder blade.
              </>
              ) : (
                <>
                  This will be recommended to be place between the spine and shoulder blade starting with <b>20 minutes, twice daily</b> as part of the home program.  The goal will be to increase the time <b>every 2 weeks by 10 minutes to the maximum of 60 minutes, twice daily</b> combined with the Eyelights and Brain Driver.  The level should be at the patients tolerance and can be increased as able to be tolerated as well. the pad will be placed on the {program === 'right' ? 'left' : 'right'} side of the upper back.
                </>
              )}
          </Typography>

          <div className="d-flex justify-content-around">
            <img
              src={`../../user-forms/doctor-assessment/exercise-images/tens_unit_${reportData.program}.jpeg`}
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>

          <a
            href="https://www.dropbox.com/scl/fi/e9knkb0w19e4onr4x0f9d/Melillo-TENS-unit-Instructions.mp4?rlkey=8mc2cvz66mbku88spef447sh0&st=v018zak9&dl=0"
            className="d-flex justify-content-center"
          >
            TENS Unit Instructional Video
          </a>
        </section>
        <Divider />

        {/* Vibration */}
        {reportData.sections.vibration.length > 0 && (
          <>
            <section className="py-3">
              <Typography variant="h3">Vibration</Typography>
              <div className="mt-2">
                <Vibration values={reportData.sections.vibration} />
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* Brain Driver/NeuroMyst */}
        {reportData.sections.brainDriver && (
          <>
            <section className="py-3">
              <Typography variant="h3">Brain Driver</Typography>
              {!reportData.isReevaluation && (<><Typography variant="body1" className="mt-2">
                <b>To use the Brain Driver, you must use a Saline Solution mixture –</b><br />
              </Typography>
                <a href="https://www.dropbox.com/scl/fi/gkai0uerilat4hpmbwqam/Melillo-Transcranial-Saline-and-Sponge-Pads.mp4?rlkey=561e4711lv9objjvtd0ngvky0&st=b8qa458y&dl=0">
                  Brain Driver Solution Instructions
                </a>
                <br />
                <a href="https://www.dropbox.com/scl/fi/ngjf293fw6tag39y26s38/Melillo-Transcranial-Direct-Current-Brain-Driver-Instructions-V2.mp4?rlkey=kwuaf7l8wtkwqmhyd3rt6i40m&st=fxqfqg3w&dl=0">
                  Brain Driver Set up Instruction Video
                </a></>)}
              <div className="mt-2">
                <BrainDriver level={reportData.sections.brainDriver} program={reportData.program} />
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* Cognitive Activities */}
        {!reportData.isReevaluation && ((reportData.sections.cognitiveActivities.rightBrain.length > 0 ||
          reportData.sections.cognitiveActivities.leftBrain.length > 0) && (
            <>
              <section className="py-3">
                <Typography variant="h3">Cognitive Activities</Typography>
                <div className="mt-2">
                  {reportData.sections.cognitiveActivities.rightBrain.length >
                    0 && (
                      <Typography variant="body1" className="mt-4">
                        <b>Right Brain</b> -{" "}
                        {reportData.sections.cognitiveActivities.rightBrain.join(
                          ", "
                        )}
                        . Age level jigsaw puzzles, the game Connect 4 and maze
                        books are right brain activities. A game of ring toss,
                        ladder ball or corn-hole are also right brain fun and can be
                        played outside or inside.
                      </Typography>
                    )}
                </div>
                <div className="mt-2">
                  {reportData.sections.cognitiveActivities.leftBrain.length >
                    0 && (
                      <Typography variant="body1" className="mt-2">
                        <b>Left Brain</b> -{" "}
                        {reportData.sections.cognitiveActivities.leftBrain.join(
                          ", "
                        )}
                        . Any type of Memory games with Numbers, letters or words
                        are left-brain activities. Games like Scrabble, Cross word
                        puzzles, Boggle and also Tic-Tac-Toe and hangman are also
                        left brain to do for fun.
                      </Typography>
                    )}
                </div>
              </section>
              <Divider />
            </>
          ))}

        {/* Vitamins */}
        {reportData.sections.vitamins.length > 0 && (
          <>
            <section className="py-3">
              <Typography variant="h3">Vitamin Recommendations</Typography>
              <div className="mt-4">
                {reportData.sections.vitamins.map((vitamin, index) => (
                  <Vitamin
                    vitamin={vitamin}
                    key={`vitamin-${index}`}
                  />
                ))}
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* Vibrant Testing */}
        {reportData.sections.vibrantTesting.length > 0 && (
          <>
            <section className="py-3">
              <Typography variant="h3">Vibrant Testing</Typography>
              <div className="mt-4">
                {reportData.sections.vibrantTesting.map((value, index) => (
                  <VibrantTesting
                    value={value}
                    key={`vibrantTesting-${index}`}
                  />
                ))}
              </div>
            </section>
            <Divider />
          </>
        )}

        <section className="py-3">
          <Typography variant="h3">Sallie</Typography>
          <Typography variant="body1" className="mt-2">
            Sallie is an online program designed to help track changes you may see, how often you are able to complete home therapy sessions and as a way to keep in touch with the Melillo Center therapists. The therapists will have access to the information you input into Sallie in order to follow along with home treatment and changes to symptoms. We encourage you to track as often as possible to give the most accurate picture of your home treatment and changes you are seeing. Feel free to use the message feature on Sallie to request the next level of tasks you need or to ask any questions our staff may be able to help you with.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src={`../../user-forms/doctor-assessment/exercise-images/sallie.png`}
              alt="exercise"
              width={120}
              className="m-4"
            />
          </div>
          <a href="https://melillomethod.powerofpatients.com" className="d-flex justify-content-center">
            Sallie Power of Patients Website
          </a>
        </section>

        {/* Ending */}
        {/* <section className="py-3">
          {reportData.isReevaluation ? (<Typography variant="body1" className="mt-2">
            It was great to see {reportData.firstName}
            {reportData.ageGroup === "Child" && "’s family"}, and check in on the progress with the program. We have updated the home report based upon the reevaluation and testing and the results will be reviewed with {reportData.ageGroup === "Child" ? "the family" : "you"}. {reportData.sections.followUpExam && `I will discuss the ongoing recommendations and recommendation for a reevaluation in ${reportData.sections.followUpExam.join(" to ")} months.`} {reportData.sections.virtualFollowUp && `I will remind them to set up a virtual follow up with the team in about ${reportData.sections.virtualFollowUp.join(" or ")} months so that we can check in on the progress and make updates as needed to the program.`}We are here to help and support {reportData.ageGroup === "Child" ? "the family" : "you"} for anything needed on the journey.
          </Typography>) :
            reportData.isVirtual ? (
              <Typography variant="body1" className="mt-2">
                It was great to meet {reportData.firstName}
                {reportData.ageGroup === "Child" && "’s family"}, and I am excited
                to get {genderObjectAdjective} started with the program because I
                believe that {genderSubjectPronoun} will do very well.{" "}
                {reportData.ageGroup === "Child" ? "The family" : "You"} will
                implement the home program right away and can reach out to us with
                any questions or assistance that is needed. I recommend that{" "}
                {reportData.ageGroup === "Child" ? "they" : "you"} schedule a
                virtual follow up in about 6-8 weeks so that we can check in on{" "}
                {reportData.ageGroup === "Child"
                  ? reportData.firstName + "'s"
                  : "your"}{" "}
                progress and make any updates that are needed. Thank you for
                allowing us to be a part of{" "}
                {reportData.ageGroup === "Child"
                  ? reportData.firstName + "'s"
                  : "your"}{" "}
                journey and we are here for any questions or additional support
                that {reportData.ageGroup === "Child" ? "they" : "you"} need while
                implementing the program.
              </Typography>
            ) : (
              <Typography variant="body1" className="mt-2">
                It was great to meet {reportData.firstName}
                {reportData.ageGroup === "Child" &&
                  " and " + genderAdjective + " family"}{" "}
                and I am excited to get {genderObjectAdjective} started with the
                program because I think that {genderSubjectPronoun} will do very
                well. The evaluation and testing results were reviewed with{" "}
                {reportData.ageGroup === "Child"
                  ? "the family"
                  : genderObjectAdjective}{" "}
                and I discussed my recommendations for therapy and treatment as
                well.{" "}
                {reportData.ageGroup === "Child" ? "They" : genderSubjectPronoun}{" "}
                will implement the home program and reach out to us with any
                questions. I have also recommended a full reevaluation in 3 to 6
                months so that we can check in the progress as well. Thank you for
                the opportunity to be a part of{" "}
                {reportData.ageGroup === "Child"
                  ? reportData.firstName + "'s"
                  : "your"}{" "}
                journey and we are excited to follow{" "}
                {reportData.ageGroup === "Child" ? genderAdjective : "your"}{" "}
                results.
              </Typography>
            )}
        </section> */}
      </main>
    </Paper>
  );


  /**
   *  Stim Report Sections
   */

  function PrimitiveReflex({
    reflex,
    level,
    genderAdjective
  }) {
    // Level 1-2: see current manual
    if (level === "1" || level === "2") {
      if (reflex === "tlr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: TLR - Level 1&2:</b> – {firstName} will start by lying flat on {genderAdjective} back, with the hands on either side. Grab the sides of the head, then flex the neck all the way up and do a quick drop back, not letting the head hit the ground. Do this <b>10 times</b>. This is similar to the Moro reflex; the difference is that if {genderSubjectPronoun} has the TLR, as you flex {genderAdjective} neck, you will see them flex {genderAdjective} arms and legs as well.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_1.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_2.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/g4x7ij4vizz1bi223jwqn/Melillo-Primitive-Reflexes-TLR-Level-1-2.mp4?rlkey=mpbpf05pdues60g9pvew0xvok&st=tq36ml8i&dl=0" className="d-flex justify-content-center">
              TLR: Level 1 – 2 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "astnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: ASTNR - Level 1 & 2:</b> – {ageGroup === "Child" ? "Parent" : "Partner"} will hold {genderAdjective} head in hands while {genderSubjectPronoun} is in a tabletop position, they will turn the head to the right and then to the left <b>10 times</b> through full range of motion, looking to see elbows bend for the reflex.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_astnr_1.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/7hwt5kl0zp0t6h1r0c73g/Melillo-Primitive-Reflexes-ATNR-Level-1-2.mp4?rlkey=sc8stkm6fusgkss9i0jwnbydr&st=80yy9xii&dl=0" className="d-flex justify-content-center">
              ASTNR: Level 1 - 2 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "babinski") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Babinski - Level 1-2:</b> – Stroke up using the handle end of a paintbrush on the lateral side and bottom of {genderAdjective} foot. Do it <b>20 times</b> on the {program === "right" ? "left" : "right"} foot and then <b>10 times</b> on the {program === "right" ? "right" : "left"} foot.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_babinski_1.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/mlgbwtyzvpfwkm63f1c8l/Melillo-Primitive-Reflexes-Babinski-Level-1-2.mp4?rlkey=8pzj19qdyyzt20919m14bo0sv&st=mln99uzx&dl=0" className="d-flex justify-content-center">
              Babinski: Level 1-2 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "rooting") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Rooting - Level 1-2:</b> Again, with a paint brush (small), stroke from cheek down to corner of mouth and then across both lips <b>5 x’s</b>; then from Chin toward corner of mouth and across lips again <b>5 x’s</b>; repeat this on the other side of face.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_rooting_1.jpeg"
                alt="exercise"
                width={200}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_rooting_2.jpeg"
                alt="exercise"
                width={200}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_rooting_3.jpeg"
                alt="exercise"
                width={200}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_rooting_4.jpeg"
                alt="exercise"
                width={200}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/jy560xcbqllcoxbxt511s/Melillo-Primitive-Reflexes-Rooting-Level-1-2.mp4?rlkey=lwggnfpubhwv17gcpnu2sqdvc&st=ef8jh7d6&dl=0" className="d-flex justify-content-center">
              Rooting: Level 1-2 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "snout") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Snout Exercise - Level 1 & 2:</b> – {ageGroup === "Child" ? "Parent" : "Partner"} will press on the Philtrum using the handle of a paint brush (space between the nose and upper lip) for <b>10 times</b>
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_snout_1.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/6w1u4o8o340bl44nymqkt/Melillo-Primitive-Reflexes-Snout-Level-1-2.mp4?rlkey=a9o2mtxy000jec83bs8p18jy6&st=oyws8npw&dl=0" className="d-flex justify-content-center">
              Snout: Level 1-2 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "spinalGalant") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Spinal Galant - Level 1 & 2:</b> – {firstName} will start on hands and knees in a tabletop position. Take a thinner brush and stroke from mid-back down on either side of the spine. Start closer to the spine, working your way out lateral to the sides, completing <b>10 reps</b>. Doing it once for each side is considered 1 rep.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_1.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_2.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/kibe28cid6mr1gij9y9am/Melillo-Primitive-Reflexes-Spinal-Galant-Level-1-2.mp4?rlkey=hhy6nt79l2le62m8cv9p8uapw&st=9jvvspay&dl=0" className="d-flex justify-content-center">
              Spinal Galant: Level 1-2 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "stnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Symmetric Tonic Neck Reflex - Level 1 & 2:</b> – {firstName} will be on {genderAdjective} hands and knees and {ageGroup === "Child" ? "Parent" : "Partner"} will hold {genderObjectAdjective} under the chin and on the crown of the head and then move {genderAdjective} head up and down quickly through full range <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_1.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_2.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/1q64c72oatvqsh978q4lp/Melillo-Primitive-Reflexes-STNR-Level-1-2.mp4?rlkey=eu82xo7asp0pe3r71mfgv4an0&st=rc3xlzln&dl=0" className="d-flex justify-content-center">
              Symmetric Tonic Neck Reflex: Level 1-2 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "moro") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Moro - Level 1-2:</b> – {firstName} will start by lying flat on {genderAdjective} back, with the hands on either side. Grab the sides of the head, then flex the neck all the way up and do a quick drop back, not letting the head hit the ground. Do this <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_1.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_2.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/uf3b9k962k4ojfl0a7zue/Melillo-Primitive-Reflexes-Moro-Level-1-2.mp4?rlkey=op54oa8fm341m09zhyoskejww&st=l4xoy8pg&dl=0" className="d-flex justify-content-center">
              Moro: Level 1-2 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "palmerGrasp") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Palmer Grasp - Level 1 & 2:</b> – Using the handle of a paint brush, {ageGroup === "Child" ? "Parent" : "Partner"}s will stimulate the reflex by drawing a “X” on the palm of {genderAdjective} hand. Do <b>20 times</b> on the {program === "right" ? "left" : "right"} hand and then <b>10 times</b> on {genderAdjective} {program === "right" ? "right" : "left"} hand.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_1.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/sbe167z5ohnxscz9pay8n/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-1-2.mp4?rlkey=ef64mkve4u6tbpnw9fy5hxdmu&st=bsaj3kdy&dl=0" className="d-flex justify-content-center">
              Palmer Grasp: Level 1-2 Instruction Video
            </a>
          </>
        );
      }
    }

    // Level 3-4: stim with suppression
    if (level === "3" || level === "4") {
      if (reflex === "tlr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: TLR – Level 3-4:</b> – {firstName} will start by lying flat on {genderAdjective} back, with the hands on either side. Grab the sides of the head, then flex the neck all the way up and do a quick drop back, not letting the head hit the ground. Instruct {genderObjectAdjective} to consciously suppress the reflex, by trying not to flex arms and legs. Do this <b>10 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_1.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_2.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/h54c6xa0sj7asgrlqioo2/Melillo-Primitive-Reflexes-TLR-Level-3-4.mp4?rlkey=fkln8bwyxrghpaxhltokb8uyi&st=a90aje96&dl=0" className="d-flex justify-content-center">
              TLR: Level 3 – 4 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "astnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: ASTNR – Level 3 & 4:</b> {ageGroup === "Child" ? "Parent" : "Partner"} will hold {genderAdjective} head in hands while {genderSubjectPronoun} is in a tabletop position, they will turn the head to the right then to the left <b>10 times</b> through full range of motion, looking to see elbows bend for the reflex. Instruct {genderObjectAdjective} to consciously suppress the reflex and not let {genderAdjective} arms bend by making them lock {genderAdjective} elbows.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_astnr_1.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/x9hlhqh9wwuvlm4xr1aq2/Melillo-Primitive-Reflexes-ATNR-Level-3-4.mp4?rlkey=6tplsb6okbfnur9xz0jc8abh3&st=4d8h9j55&dl=0" className="d-flex justify-content-center">
              ASTNR: Level 3 - 4 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "babinski") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex:  Babinski – Level 3-4</b> – Stroke up using the handle end of a paintbrush or a Rezzimax on the lateral side and bottom of {genderAdjective} foot. Instruct {genderObjectAdjective} to consciously suppress the reflex by asking them to curl {genderAdjective} toes forward. For a {program} brain program do it <b>20 times</b> on the {program === "left" ? "right" : "left"} foot and then <b>10 times</b> on the {program === "left" ? "left" : "right"} foot.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_babinski_1.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/sgr0uay272cnitof1h5zz/Melillo-Primitive-Reflexes-Babinski-Level-3-4.mp4?rlkey=qa3d3ns95wu575o4wdb6t4sfq&st=xnty9lj2&dl=0" className="d-flex justify-content-center">
              Babinski: Level 3-4 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "rooting") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Rooting – Level 3-4</b> Again, with a paint brush (small) or a Rezzimax, take a brush stroke from cheek down to corner of mouth and then across both lips <b>5 x’s</b>; then from Chin toward corner of mouth and across lips again <b>5 x’s</b>; repeat this on the other side of face. Instruct {genderObjectAdjective} to consciously suppress the reflex by trying not to let {genderAdjective} lips or mouth contract in any way.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_rooting_1.jpeg"
                alt="exercise"
                width={200}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_rooting_2.jpeg"
                alt="exercise"
                width={200}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_rooting_3.jpeg"
                alt="exercise"
                width={200}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_rooting_4.jpeg"
                alt="exercise"
                width={200}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/nuap2uojzngf4fxrpsrew/Melillo-Primitive-Reflexes-Rooting-Level-3-4.mp4?rlkey=60b8hris61g6xeuyix8dlnzik&st=dm18mw7i&dl=0" className="d-flex justify-content-center">
              Rooting: Level 3-4 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "snout") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Snout Exercise – Level 3 & 4:</b> {ageGroup === "Child" ? "Parent" : "Partner"} will press on the Philtrum using the handle of a paint brush or a Rezzimax (space between the nose and upper lip) for <b>10 times</b>. Instruct {genderObjectAdjective} to consciously suppress the reflex by not letting {genderAdjective} lips contract.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_snout_1.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/sol2ddrcdaq0lips4nj94/Melillo-Primitive-Reflexes-Snout-Level-3-4.mp4?rlkey=9qe5haucvgjws507w50mfdk3l&st=ekhvfi5p&dl=0" className="d-flex justify-content-center">
              Snout: Level 3-4 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "spinalGalant") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Spinal Galant - Level 3 & 4:</b> {firstName} will start on hands and knees in a tabletop position. Take a thinner brush or a Rezzimax and stroke from mid-back down on either side of the spine. Start closer to the spine, working your way out lateral to the sides, completing <b>10 reps</b>. Doing it once for each side is considered 1 rep. Instruct {genderObjectAdjective} to consciously suppress the reflex by not having {genderAdjective} back twitch or flex.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_1.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_2.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/pynp54lhp59822mq1s3ui/Melillo-Primitive-Reflexes-Spinal-Galant-Level-3-4.mp4?rlkey=lphh0bblah5b2f35k9obhxoyy&st=2r437bly&dl=0" className="d-flex justify-content-center">
              Spinal Galant: Level 3-4 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "stnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Symmetric Tonic Neck - Level 3 & 4: </b> {firstName} will be on {genderAdjective} hands and knees and {ageGroup === "Child" ? "Parent" : "Partner"} will hold {genderObjectAdjective} under the chin and on the crown of the head, and then move {genderAdjective} head up and down quickly through full range <b>10 times</b>. Instruct {genderObjectAdjective} to consciously suppress the reflex by not letting {genderAdjective} arms bend as you bring {genderAdjective} head down, or shift backwards as you bring {genderAdjective} head up.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_1.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_2.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/i2d09rnwn8yn4wj374aba/Melillo-Primitive-Reflexes-STNR-Level-3-4.mp4?rlkey=ir71y9xi4buhbtoqauusd2529&st=bk222wnw&dl=0" className="d-flex justify-content-center">
              Symmetric Tonic Neck: Level 3-4 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "moro") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Moro – Level 3-4</b> – {firstName} will be lying flat on back, with the hands on either side. Kneel or sit behind and hold the sides of the head.  Flex the neck all the way up and do a quick drop back, not letting the head hit the ground. Instruct {genderObjectAdjective} to consciously suppress the reflex by not letting {genderAdjective} arms and legs twitch. Do this <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_1.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_2.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/uzmidakp3zssgnl9lnpcp/Melillo-Primitive-Reflexes-Moro-Level-3-4_v3.mp4?rlkey=bkztxfvnqxzn5apap98s8jejz&st=77bz4w5y&dl=0" className="d-flex justify-content-center">
              Moro: Level 3-4 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "palmerGrasp") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Palmer Grasp – Level 3-4</b> – Using a Rezzimax or a handle of a paint brush, {ageGroup === "Child" ? "Parent" : "Partner"}s will stimulate the reflex by drawing a “X” on the palm of the hand. Instruct {genderObjectAdjective} to consciously suppress the reflex by not letting {genderAdjective} hands flex. For a {program} brain program, this will be <b>20 times</b> on the {program === "left" ? "right" : "left"} hand and then <b>10 times</b> on the {program === "left" ? "left" : "right"} hand.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_1.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/tci0zpzhbyxuufbyd0lkh/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-3-4.mp4?rlkey=q5n24ergr4mt73cfcpwf9hfsf&st=qwdazpk7&dl=0" className="d-flex justify-content-center">
              Palmer Grasp: Level 3-4 Instruction Video
            </a>
          </>
        );
      }
    }

    // Level 5
    if (level === "5") {
      if (reflex === "tlr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>TLR - Level 5 (Cannonball):</b>  In a supine position, hold knees into chest and wrap arms around legs. Bring your head up to your knees so that your shoulders should leave the ground as you curl into a tight ball and roll forward into a sitting position; then roll back while extending the neck. Do this for <b>10 reps</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_3.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_4.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/gyxb95keiieiahlt1mkwl/Melillo-Primitive-Reflexes-TLR-Level-5.mp4?rlkey=bmrgegct6eq68auztnv0tnu3w&st=h7ixp3as&dl=0" className="d-flex justify-content-center">
              TLR: Level 5 – Cannonball instruction Video
            </a>
          </>

        );
      }
      if (reflex === "astnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitiven Reflex: ASTNR - Level 5 (Lizard):</b>Lying on stomach, head turned to one side, the arm and leg are extended straight on the side the head is turned towards; arm and leg on other side are flexed. Then lift the head turn to the opposite side; at the same time flex the arm and leg that were extended and extend the opposite arm and leg. Then turn head again back to original position and arms and legs go back to starting points. This is one rep. Do this for <b>10 reps</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_astnr_2.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_astnr_3.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/h73bdc2ma82nhljr21fby/Melillo-Primitive-Reflexes-ATNR-Level-5.mp4?rlkey=mtll9f38kz8r3hbn2dcyug5cf&st=g62qizgb&dl=0" className="d-flex justify-content-center">
              ASTNR: Level 5 - Lizard Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "babinski") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex:  Babinski - Level 5:</b> Using a tennis ball on the bottom of your foot, you will roll the tennis ball back and forth on the outer edge of the foot. Flex the toes down as you roll the ball forward and extend your toes up as you roll the ball back towards the heel. Repeat this exercise for <b>10 reps</b> on the {program} foot and <b>20 reps</b> on the {program === "left" ? "right" : "left"} foot for a {program} brain program.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_babinski_2.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_babinski_3.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/vi21ep2q03yvc5q2po8f6/Melillo-Primitive-Reflexes-Babinski-Level-5.mp4?rlkey=5i22o1ne3e2gme2efhdxgsm13&st=p6uc0dmg&dl=0" className="d-flex justify-content-center">
              Babinski: Level 5 Instruction Video
            </a>
          </>

        );
      }
      if (reflex === "palmerGrasp") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Palmer Grasp - Level 5:</b>Using a tennis ball, you will squeeze the ball in your hand.  For a {program} brain program, you will do this for <b>20 times</b> in the {program === "left" ? "right" : "left"} hand and <b>10 times</b> with the {program === "left" ? "left" : "right"} hand.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_2.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/dmovdk7cj9dtkfiaqacay/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-5.mp4?rlkey=7swe749f8qr67x7a1xxmrtz92&st=spnqb303&dl=0" className="d-flex justify-content-center">
              Palmer Grasp: Level 5 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "rooting") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Rooting - Level 5:</b> {genderObjectAdjective} will use a brush to independently stimulate the rooting reflex as done in levels 1-4. Take a brush and stroke from cheek down to corner of mouth and then across both lips <b>5 x’s</b>; then from Chin toward corner of mouth and across lips again <b>5 x’s</b>; repeat this on the other side of face.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_rooting_5.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_rooting_6.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/kyu34u7nbvaee3oncumjx/Melillo-Primitive-Reflexes-Rooting-Level-5.mp4?rlkey=hpaahn6bjstiu4efizokbu0yh&st=yd39f6vl&dl=0" className="d-flex justify-content-center">
              Rooting: Level 5 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "snout") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Snout Exercise - Level 5:</b> Press the brush in the center of both lips; {firstName} should be able to complete this independently for <b>10 reps</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_snout_2.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/drizxsdsfvx6w1lc8thgt/Melillo-Primitive-Reflexes-Snout-Level-5.mp4?rlkey=kkjyxo0ce42fuhb35h9lq3obv&st=kse5pb5m&dl=0" className="d-flex justify-content-center">
              Snout: Level 5 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "spinalGalant") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Spinal Galant - Level 5 (Angels in Snow):</b> Lying on the back, start with arms at the side and legs together. Then, opening arms and legs together until hands touch at the top and legs are spread as far as possible apart at the same time.  Then go back to original position with hands next to body and legs together, that is 1 rep. Do this for <b>10 reps</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_3.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_4.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/cz0untwa641ecsiu4gx7c/Melillo-Primitive-Reflexes-Spinal-Galant-Level-5.mp4?rlkey=t5v1pcyoe7dop5boxo43fjv5d&st=i091g6ql&dl=0" className="d-flex justify-content-center">
              Spinal Galant: Level 5 – Angels in the Snow Instruction Video
            </a>
          </>

        );
      }
      if (reflex === "stnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Symmetric Tonic Neck - Level 5 (Cat Stretch):</b> On hands and knees in a tabletop position, move the head all of the way up until neck is fully extended while arching the lower back down at the same time; then flex the neck down with the chin close to chest and eyes looking at knees while arching the back up like a cat.  Complete through the full range up and down for <b>10 reps</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_3.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_4.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/j5ql4w9cq1lvrb3yjf8pz/Melillo-Primitive-Reflexes-STNR-Level-5.mp4?rlkey=qnca348ai4q413erd4rvjqed5&st=jwc1goji&dl=0" className="d-flex justify-content-center">
              Symmetric Tonic Neck: Level 5 – Cat Stretch Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "moro") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Moro – Level 5 (Starfish):</b>Sitting on chair, start from fetal position, the right wrist over left wrist and the right ankle over left (crisscrossed).  Open all the way up, extending head all the way back with arms, hands and legs stretched out to full extension, then return to the starting position but this time crossing left wrist over right and left ankle over right. Doing this on each side is considered 1 rep.  Repeat and do this for a total of <b>10 reps</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_1.jpeg"
                alt="exercise"
                width={220}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_2.jpeg"
                alt="exercise"
                width={220}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_3.jpeg"
                alt="exercise"
                width={220}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/wlulyez2r47mdr5bpsyl6/Melillo-Primitive-Reflexes-Moro-Level-5_v3.mp4?rlkey=v5w4wl86ppifk0qbo3th5858a&st=emqdathh&dl=0" className="d-flex justify-content-center">
              Moro: Level 5 – Starfish Instruction Video
            </a>
          </>
        );
      }
    }

    // Level 6
    if (level === "6") {
      if (reflex === "tlr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: TLR - Level 6:</b> Start by laying on your back with legs and arms extended straight. Flex your legs into a crunch position as you bring your head forward. As you extend back out, bring your head back down to the floor. Repeat <b>10 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_5.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/1nmip1xxrmjnu8sfey3md/Melillo-Primitive-Reflexes-TLR-Level-6.mp4?rlkey=1wctehbk0e7kalvue1nvr2uki&st=m8iid52i&dl=0" className="d-flex justify-content-center">
              TLR: Level 6 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "astnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: ASTNR - Level 6:</b> Start by laying on your back. When crunching up bring your knee up and touch it to your elbow on the same side; while doing this your head should be turned away from the arm and leg that are up. Alternate side to side, <b>10 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_astnr_4.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/k3zbpaxa3tfpangzperyi/Melillo-Primitive-Reflexes-ATNR-Level-6.mp4?rlkey=ykxirgmtargd7ng4tdlp4e9g2&st=zphxdo4w&dl=0" className="d-flex justify-content-center">
              ASTNR: Level 6 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "babinski") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Babinski - Level 6:</b> With bare feet, stand on the floor and raise up onto the balls of your feet and back down; repeat this <b>10 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_babinski_4.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_babinski_5.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/in371t5bcjzen6gkj26q7/Melillo-Primitive-Reflexes-Babinski-Level-6.mp4?rlkey=zo0usivdrdj2b7cra2n16z1om&st=6rtx8fia&dl=0" className="d-flex justify-content-center">
              Babinski: Level 6 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "palmerGrasp") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b> Primitive Reflex: Palmer Grasp – Level 6:</b> Squeeze a hand gripper; once hand is completely flexed hold and resist the gripper from opening back up; repeat <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_3.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/bhq5wc9sloe358r0xzypy/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-6.mp4?rlkey=lygp7srunmfmmhcspez30t5nd&st=oi23qcsd&dl=0" className="d-flex justify-content-center">
              Palmer Grasp: Level 6 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "moro") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Moro – Level 6:</b> Start laying on your back in a crunch position with arms and legs crossed. Be sure to have the same arm and leg crossed over the other. As you move into extension from a crunched position, extend both arms and legs out in a starfish position. When flexing back into a crunched position, cross your arms and legs in the opposite direction. Repeat this <b>10 times</b>, alternating the direction your arms and legs are crossed each time.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_4.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_5.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/v3w6p23x8oq8o0x5lg444/Melillo-Primitive-Reflexes-Moro-Level-6_v3.mp4?rlkey=wmrk3rx84u8w8jp3jy4pfnfko&st=7lkqnllh&dl=0" className="d-flex justify-content-center">
              Moro: Level 6 Instruction Video
            </a>
          </>
        );
      }

      if (reflex === "spinalGalant") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Spinal Galant – Level 6:</b>  In a side plank position with both arms and both legs on the ground, raise core up and back down. Repeat <b>10 times</b> on both sides.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_5.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_6.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/q5nqo6mdsvenxa2ivm418/Melillo-Primitive-Reflexes-Spinal-Galant-Level-6.mp4?rlkey=f6go6nr8ypyjcsax97rvbnkgz&st=g4fx13ew&dl=0" className="d-flex justify-content-center">
              Spinal Galant: Level 6 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "stnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Symmetric Tonic Neck – Level 6:</b> Start by laying on the floor with legs flexed into a cannonball position, hold knees in flexion with both arms. Roll back and forth. Move the head up when rolling forward and down when rolling back; repeat this <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_5.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_6.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/40hvv9imnqbqtuf0u7o46/Melillo-Primitive-Reflexes-STNR-Level-6.mp4?rlkey=ob0gurprm1gk5jmizfuosn4cw&st=k7dvx8xy&dl=0" className="d-flex justify-content-center">
              STNR: Level 6 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "rooting") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Rooting – Level 6:</b> Using a vibrating toothbrush or Rezzimax, repeat the same motion as the rooting reflex stimulation. Stroke from cheek down to corner of mouth and then across both lips <b>5 x’s</b>; then from Chin toward corner of mouth and across lips again <b>5 x’s</b>; repeat this on the other side of face.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_rooting_7.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/4qj4hmy9hlssxz0o54dz6/Melillo-Primitive-Reflexes-Rooting-Level-6.mp4?rlkey=bpjqiog08x1ic5s0o2s4yi674&st=ffn2ln9c&dl=0" className="d-flex justify-content-center">
              Rooting: Level 6 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "snout") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Snout Exercise – Level 6: </b>Using a vibrating toothbrush or Rezzimax, press the vibration tool to the center of the lips in the same fashion as previous levels. Repeat this for <b>10 reps</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_snout_3.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/qwnvhvthi7yg1q4ymmrmh/Melillo-Primitive-Reflexes-Snout-Level-6.mp4?rlkey=qffbg9irx01l27awnbxm0j2cc&st=oyh7t88e&dl=0" className="d-flex justify-content-center">
              Snout: Level 6 Instruction Video
            </a>
          </>
        );
      }
    }

    // Level 7
    if (level === "7") {
      if (reflex === "tlr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: TLR – Level 7:</b> Starting position should be sitting on the floor with knees bent, arms on the floor at your sides with fingers pointing behind you with the head flexed down. For each repetition, torso should be extended and head tilted back as you extend, and then back down; when flexing back down, backside should not touch the ground. Repeat <b>10 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_6.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_7.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/gee9wsq48x27m8koe4h7u/Melillo-Primitive-Reflexes-TLR-Level-7.mp4?rlkey=408i4x2yyb05dolnnng5dmjsf&st=bf8j7rkq&dl=0" className="d-flex justify-content-center">
              TLR: Level 7 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "astnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: ASTNR - Level 7:</b> Lunge forward with one knee, touch elbow on the same side to the knee and extend the arm and leg on the opposite side; turn head towards the extended side. Alternate sides, one lunge to the right and one to the left counts as one rep. Repeat <b>10 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_astnr_5.jpeg"
                alt="exercise"
                width={220}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/wl3u1lf5sn58sbh8v41w1/Melillo-Primitive-Reflexes-ATNR-Level-7.mp4?rlkey=v03pcocd09xta8qwrm190fvqw&st=1ezhar56&dl=0" className="d-flex justify-content-center">
              ASTNR: Level 7 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "babinski") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Babinski - Level 7:</b> With bare feet, stand on the floor and raise up onto the balls of your feet and back down; as you lower your heels back down to the floor do a squat and then raise back up on your toes on the way up; repeat <b>20 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_babinski_6.jpeg"
                alt="exercise"
                width={150}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_babinski_7.jpeg"
                alt="exercise"
                width={150}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/jjwt4v0oufkye1jcvu936/Melillo-Primitive-Reflexes-Babinski-Level-7.mp4?rlkey=9mykc57nwf1p5nn6gglqvkcbf&st=wlgrqovj&dl=0" className="d-flex justify-content-center">
              Babinski: Level 7 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "palmerGrasp") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Palmer Grasp - Level 7:</b> Using a rubber band or rubber band extensors, extend fingers and thumb until hand is completely open, the rubber band will create resistance to this motion. Repeat <b>10 times</b> on each hand.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_4.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/wm6nz13al7ryy6z3slg75/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-7.mp4?rlkey=387yz7tmxk31uk5u00u2vulgn&st=nd310unc&dl=0" className="d-flex justify-content-center">
              Palmer Grasp: Level 7 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "moro") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Moro - Level 7:</b> Start laying on your back in a crunch position with arms and legs crossed on the same side. As you move into extension from a crunched position, extend both arms and legs out in a starfish position. Once fully extended, roll onto your stomach and then back onto your back before flexing back into a crunch position. When flexing back into a crunched position, cross your arms and legs in the opposite direction that they started in. Repeat this <b>10 times</b> on each side, alternating the direction your arms and legs are crossed and the side you are rolling to.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_6.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_7.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_8.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_9.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/nrkxihpo2iyb8weh4hzgw/Melillo-Primitive-Reflexes-Moro-Level-7_v3.mp4?rlkey=a8eogzporjm4viqv7z6pd3y0b&st=h6op25s3&dl=0" className="d-flex justify-content-center">
              Moro: Level 7 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "spinalGalant") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Spinal Galant - Level 7:</b> Starting position is a side plank with feet stacked on top of one another with both hands on the ground. As you raise your torso off the ground, raise the top leg off the other. Repeat <b>10 times</b> on each side.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_7.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/1vvcq7ay8tp0wdv5flh3g/Melillo-Primitive-Reflexes-Spinal-Galant-Level-7.mp4?rlkey=u5strcsp0xae8pqw3kcuw8jwu&st=kydjpxkk&dl=0" className="d-flex justify-content-center">
              Spinal Galant: Level 7 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "stnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Symmetric Tonic Neck - Level 7:</b> Starting position is crouched down with knees bent and hands on the floor in front of you with head flexed down. Jump out into a plank position and extend the head at the same time. Then jump back into the starting position and flex the head at the same time. Repeat <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_7.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_8.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/b9f39uckmnrbavtsali02/Melillo-Primitive-Reflexes-STNR-Level-7.mp4?rlkey=tukbzi12bm4ywfbdwgw102yn4&st=u88hmxuf&dl=0" className="d-flex justify-content-center">
              STNR: Level 7 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "rooting" || reflex === "snout") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: {reflex === "rooting" ? "Rooting" : "Snout"} - Level 7:</b> Suck lips all the way in and then blow out making a popping noise. Repeat <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_snout_4.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_snout_5.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/skq1npvn6vy2j26wypin6/Melillo-Primitive-Reflexes-Rooting-Snout-Level-7.mp4?rlkey=ari5ohpsthvdt0nvgl0siq84h&st=hu34k2gy&dl=0" className="d-flex justify-content-center">
              Rooting & Snout: Level 7 Instruction Video
            </a>
          </>
        );
      }
    }

    // Level 8
    if (level === "8") {
      if (reflex === "tlr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: TLR - Level 8:</b> Stand with feet together and arms at your sides, squat down and flex head forward, as you stand up extend head all the way back. Repeat this <b>10 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_8.jpeg"
                alt="exercise"
                width={200}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_9.jpeg"
                alt="exercise"
                width={200}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/jhqobvxlzxt4purfuro7t/Melillo-Primitive-Reflexes-TLR-Level-8.mp4?rlkey=e4gml4b8luvf8un7d3bf2mch4&st=prj7l7jg&dl=0" className="d-flex justify-content-center">
              TLR: Level 8 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "astnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: ASTNR - Level 8:</b> Start with feet shoulder width apart and legs slightly bent with arms at your sides. Step back and behind the other foot with one leg bringing your elbow to touch the knee of your front leg. Extend your opposite arm out and turn head towards the extended side. Then jump across and extend the opposite arm and leg and turn your head in the other direction. Repeat this <b>10 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_astnr_6.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/den69dxm066hxwbxexjke/Melillo-Primitive-Reflexes-ATNR-Level-8.mp4?rlkey=6q0oejlzrubbqkwfcobrz9vnv&st=aud60rrj&dl=0" className="d-flex justify-content-center">
              ASTNR: Level 8 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "babinski") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Babinski - Level 8:</b> With bare feet, stand on the floor and raise up onto the balls of your feet and back down; as you lower your heels back down to the floor do a squat and then raise back up on your toes on the way up and jump up. Repeat <b>20 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_babinski_8.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_babinski_9.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/116jus3c40h6brthye3gw/Melillo-Primitive-Reflexes-Babinski-Level-8.mp4?rlkey=cwhu979xoky80xwnatewwmxcx&st=b89896u5&dl=0" className="d-flex justify-content-center">
              Babinski: Level 8 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "palmerGrasp") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Palmer Grasp - Level 8:</b> Start in a standing position, lean forward and touch the floor so your palms are flat on the ground, walk forward on your hands into a plank position, then walk back and stand back up. Repeat this <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_5.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_6.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/a6he6nqy9iauep346z38n/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-8_v3.mp4?rlkey=ulf70c80tkstdhw1k3fxpnx5n&st=u0arwbe7&dl=0" className="d-flex justify-content-center">
              Palmer Grasp: Level 8 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "moro") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Moro - Level 8:</b> Squat down with arms crossed and fists touching the ground, head flexed. As you stand up, extend arms out and head back, then squat back down, cross arms in front of you and flex your head forward. Repeat <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_10.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_11.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/qytvcms0gd2xyctfgukre/Melillo-Primitive-Reflexes-Moro-Level-8_v3.mp4?rlkey=fajo8lc5jh4328r6wj8zi3cqc&st=3h63mfak&dl=0" className="d-flex justify-content-center">
              Moro: Level 8 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "spinalGalant") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Spinal Galant - Level 8:</b> In a standing position, do side crunches, alternating side to side, bringing the knee up to touch your elbow. Repeat this <b>20 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_8.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/u37k9wl24h33buupjwqph/Melillo-Primitive-Reflexes-Spinal-Galant-Level-8.mp4?rlkey=1dbpti2v5viz6zuw1xitth9tx&st=y00mam3f&dl=0" className="d-flex justify-content-center">
              Spinal Galant: Level 8 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "stnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Symmetric Tonic Neck - Level 8:</b> Start in a standing position, squat down so hands are flat on the ground and flex head forward(down), then jump out into a plank position and extend head back(up); repeat this <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_9.jpeg"
                alt="exercise"
                width={220}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_10.jpeg"
                alt="exercise"
                width={220}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/qpjuc3wa4vxz03rs75ex8/Melillo-Primitive-Reflexes-STNR-Level-8_v3.mp4?rlkey=tmxr5kgfisabr03r3apsil8e6&st=8frhbm7s&dl=0" className="d-flex justify-content-center">
              STNR: Level 8 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "rooting" || reflex === "snout") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: {reflex === "rooting" ? "Rooting" : "Snout"} - Level 8:</b> Put thumb in mouth and blow out hard so cheeks puff out, then take thumb out of mouth and open mouth wide while contracting all the muscles around the mouth and upper neck (platysma); repeat this <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_snout_6.jpeg"
                alt="exercise"
                width={220}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_snout_7.jpeg"
                alt="exercise"
                width={220}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/d40bizkcay5c6rh0p70kt/Melillo-Primitive-Reflexes-Rooting-Snout-Level-8.mp4?rlkey=xht0394x4e5rlnz6ahljqzoi0&st=9e5d4k8w&dl=0" className="d-flex justify-content-center">
              Rooting & Snout: Level 8 Instruction Video
            </a>
          </>
        );
      }
    }
    // Level 9
    if (level === "9") {
      if (reflex === "tlr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: TLR - Level 9:</b> Stand with feet together and arms at your sides, squat down and flex head forward, then jump up and extend head all the way back. Repeat this <b>10 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_8.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_9.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/y7q0cqf4ir0l4z3mgy77j/Melillo-Primitive-Reflexes-TLR-Level-9-10.mp4?rlkey=spmbs43bt9setpspji3k87eci&st=bszipguh&dl=0" className="d-flex justify-content-center">
              TLR: Level 9 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "astnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: ASTNR - Level 9:</b> Start with feet shoulder width apart and legs slightly bent with arms at your sides. Step back and behind the other foot with one leg, bringing your opposite elbow to touch the knee of your front leg. Extend your opposite arm out and turn head towards the flexed side. Then jump across and extend the opposite arm and leg and turn your head in the other direction. Repeat this <b>10 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_astnr_7.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/6rrceealcw8fpau4m4jym/Melillo-Primitive-Reflexes-ATNR-Level-9-10.mp4?rlkey=12xmyibhoyhyv18pkjpwvgh9l&st=49pfa2w4&dl=0" className="d-flex justify-content-center">
              ASTNR: Level 9 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "babinski") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Babinski - Level 9:</b> Jump rope for <b>20 repetitions</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_babinski_10.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/177wx5m7pigtzs6ktxb5k/Melillo-Primitive-Reflexes-Babinski-Level-9-10.mp4?rlkey=9gk7ua46rdev2iiw2mcr5pl11&st=dje2q5f5&dl=0" className="d-flex justify-content-center">
              Babinski: Level 9 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "palmerGrasp") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Palmer Grasp - Level 9:</b> Start in a standing position, lean forward and touch the floor so your palms are flat on the ground, walk forward on your hands into a plank position, do a push up, then walk back and stand back up. Repeat this <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_5.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_6.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_7.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/fclznlbsugak0uujnyenz/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-9-10.mp4?rlkey=7wz0dlczhzqlecanmb87wl9s9&st=j4cg9l5s&dl=0" className="d-flex justify-content-center">
              Palmer Grasp: Level 9 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "moro") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Moro - Level 9:</b> Squat down with arms crossed and fists touching the ground, head flexed. Then jump up, extend arms out and head back, then squat back down, cross arms in front of you and flex your head forward. Repeat <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_12.jpeg"
                alt="exercise"
                width={220}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_13.jpeg"
                alt="exercise"
                width={220}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/0kus4ri62wpt0dscmjxqw/Melillo-Primitive-Reflexes-Moro-Level-9-10_v3.mp4?rlkey=cnhgjf3uv4lv7vj27ky5htchg&st=fxfg5ssk&dl=0" className="d-flex justify-content-center">
              Moro: Level 9 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "spinalGalant") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Spinal Galant - Level 9:</b> Do <b>20 jumping jacks</b>; be sure to move your arms and legs at the same pace.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_9.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_10.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/9brkhntyuej4puvqiv1fl/Melillo-Primitive-Reflexes-Spinal-Galant-Level-9-10.mp4?rlkey=o6gmopur8a496uccg0f3tqq68&st=dh6yopz1&dl=0" className="d-flex justify-content-center">
              Spinal Galant: Level 9 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "stnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Symmetric Tonic Neck - Level 9:</b> Start in a standing position, squat down so hands are flat on the ground and flex head forward, then jump out into a plank position and extend head back, while in this position do a push up, then jump back into a crouch position and jump up, extending the head back as you do this. Repeat this <b>10 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_11.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_12.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_13.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_14.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/wle1nsvmgj966116r5x46/Melillo-Primitive-Reflexes-STNR-Level-9-10.mp4?rlkey=dq9wp5qhyloapjf7k2xhr5j74&st=28bfs5fg&dl=0" className="d-flex justify-content-center">
              STNR: Level 9 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "rooting" || reflex === "snout") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: {reflex === "rooting" ? "Rooting" : "Snout"} - Level 9:</b> Blow up a balloon <b>5 times</b>, blowing out as long as you can and keeping a seal on the balloon with your mouth.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_snout_8.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/d1mm5os8rhkn1jptdnwi7/Melillo-Primitive-Reflexes-Rooting-Snout-Level-9-10_v3.mp4?rlkey=ck11ogbi4pqnts06f2x0jtlb4&st=rgxswh9n&dl=0" className="d-flex justify-content-center">
              Rooting & Snout: Level 9 Instruction Video
            </a>
          </>
        );
      }
    }

    // level 10
    if (level === "10") {
      if (reflex === "tlr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>TLR - Level 10:</b> Stand with feet together and arms at your sides, squat down and flex head forward, then jump up and extend head all the way back. Repeat this <b>20 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_8.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_tlr_9.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/y7q0cqf4ir0l4z3mgy77j/Melillo-Primitive-Reflexes-TLR-Level-9-10.mp4?rlkey=spmbs43bt9setpspji3k87eci&st=bszipguh&dl=0" className="d-flex justify-content-center">
              TLR: Level 10 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "astnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: ASTNR - Level 10:</b> Start with feet shoulder width apart and legs slightly bent with arms at your sides. Step back and behind the other foot with one leg, bringing your opposite elbow to touch the knee of your front leg. Extend your opposite arm out and turn head towards the flexed side. Then jump across and extend the opposite arm and leg and turn your head in the other direction. Repeat this <b>20 times</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_astnr_7.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/6rrceealcw8fpau4m4jym/Melillo-Primitive-Reflexes-ATNR-Level-9-10.mp4?rlkey=12xmyibhoyhyv18pkjpwvgh9l&st=49pfa2w4&dl=0" className="d-flex justify-content-center">
              ASTNR: Level 10 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "babinski") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Babinski - Level 10:</b> Jump rope for <b>40 repetitions</b>.
            </Typography>
            <div className="d-flex justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_babinski_10.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/177wx5m7pigtzs6ktxb5k/Melillo-Primitive-Reflexes-Babinski-Level-9-10.mp4?rlkey=9gk7ua46rdev2iiw2mcr5pl11&st=dje2q5f5&dl=0" className="d-flex justify-content-center">
              Babinski: Level 10 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "palmerGrasp") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Palmer Grasp - Level 10:</b> Start in a standing position, lean forward and touch the floor so your palms are flat on the ground, walk forward on your hands into a plank position, do a push up, then walk back and stand back up. Repeat this <b>20 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_5.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_6.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_palmer_7.jpeg"
                alt="exercise"
                width={250}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/fclznlbsugak0uujnyenz/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-9-10.mp4?rlkey=7wz0dlczhzqlecanmb87wl9s9&st=j4cg9l5s&dl=0" className="d-flex justify-content-center">
              Palmer Grasp: Level 10 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "moro") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Moro - Level 10:</b> Squat down with arms crossed and fists touching the ground, head flexed. Then jump up, extend arms out and head back, then squat back down, cross arms in front of you and flex your head forward. Repeat <b>20 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_12.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_moro_13.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/0kus4ri62wpt0dscmjxqw/Melillo-Primitive-Reflexes-Moro-Level-9-10_v3.mp4?rlkey=cnhgjf3uv4lv7vj27ky5htchg&st=fxfg5ssk&dl=0" className="d-flex justify-content-center">
              Moro: Level 10 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "spinalGalant") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Spinal Galant - Level 10:</b> Do <b>40 jumping jacks</b>; be sure to move your arms and legs at the same pace.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_9.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_spinal_10.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/9brkhntyuej4puvqiv1fl/Melillo-Primitive-Reflexes-Spinal-Galant-Level-9-10.mp4?rlkey=o6gmopur8a496uccg0f3tqq68&st=dh6yopz1&dl=0" className="d-flex justify-content-center">
              Spinal Galant: Level 10 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "stnr") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: Symmetric Tonic Neck - Level 10:</b> Start in a standing position, squat down so hands are flat on the ground and flex head forward, then jump out into a plank position and extend head back, while in this position do a push up, then jump back into a crouch position and jump up, extending the head back as you do this. Repeat this <b>20 times</b>.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_11.jpeg"
                alt="exercise"
                width={180}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_12.jpeg"
                alt="exercise"
                width={180}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_13.jpeg"
                alt="exercise"
                width={180}
                className="m-3"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_stnr_14.jpeg"
                alt="exercise"
                width={180}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/wle1nsvmgj966116r5x46/Melillo-Primitive-Reflexes-STNR-Level-9-10.mp4?rlkey=dq9wp5qhyloapjf7k2xhr5j74&st=28bfs5fg&dl=0" className="d-flex justify-content-center">
              STNR: Level 10 Instruction Video
            </a>
          </>
        );
      }
      if (reflex === "rooting" || reflex === "snout") {
        return (
          <>
            <Typography variant="body1" className="mt-2">
              <b>Primitive Reflex: {reflex === "rooting" ? "Rooting" : "Snout Exercise"} - Level 10:</b> Blow up a balloon <b>10 times</b>, blowing out as long as you can and keeping a seal on the balloon with your mouth.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/pr/pr_snout_8.jpeg"
                alt="exercise"
                width={300}
                className="m-3"
              />
            </div>
            <a href="https://www.dropbox.com/scl/fi/d1mm5os8rhkn1jptdnwi7/Melillo-Primitive-Reflexes-Rooting-Snout-Level-9-10_v3.mp4?rlkey=ck11ogbi4pqnts06f2x0jtlb4&st=rgxswh9n&dl=0" className="d-flex justify-content-center">
              Rooting & Snout: Level 10 Instruction Video
            </a>
          </>
        );
      }
    }

    return null;
  }

  function VestibularSpinning({ level }) {
    // Helper for direction
    const direction = program === "left" ? "counter-clockwise" : "clockwise";
    const isGalvanicOrShokz = reportData.sections.galvanic.length > 0 || reportData.sections.shokz.length > 0;

    if (level === "1") {
      return (
        <Typography variant="body1" component="div">
          <b>Level 1: Spinning:</b> {ageGroup === "Child" ? "Parents" : "Partner"} will do spinning – {firstName} will sit in a chair and at this level, may need {ageGroup === "Child" ? "parent" : "partner"} to assist with this exercise by holding {genderObjectAdjective}. {ageGroup === "Child" ? "Parent" : "Partner"} will spin and go Fast to the {program}, {direction} for <b>2 seconds per rotation</b> for as long as {genderSubjectPronoun} will tolerate and repeat if possible until you can see Nystagmus (beating) of the eyes when stopped.
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/vestibular/image3.jpeg"
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/7bxxfvto4u99cmqknjpua/Melillo-Visual-Testing-Vestibular-L1.mp4?rlkey=xopojlylpursaaqcnxce21w89&st=fvwbcskr&dl=0" className="d-flex justify-content-center">Level 1 Spinning Instructions</a>
        </Typography>
      );
    }

    if (level === "2") {
      return (
        <Typography variant="body1" component="div">
          <b>Level 2:  Spinning:</b> {ageGroup === "Child" ? "Parents" : "Partner"} will do spinning – {firstName} will sit in a chair and at this level, preferably with feet crossed. {ageGroup === "Child" ? "Parent" : "Partner"} will spin and go Fast to the {program}, {direction} for <b>2 seconds per rotation</b> for as long as ${genderSubjectPronoun} will tolerate for <b>10 – 50 rotations</b> and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped.
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/vestibular/image4.jpeg"
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/q7okktw8nffbe8ftgybs7/Melillo-Visual-Testing-Vestibular-L2.mp4?rlkey=da5j50046m91s2hk5blweuqfg&st=9hvou9ts&dl=0" className="d-flex justify-content-center">Level 2 Spinning Instructions</a>
        </Typography>
      );
    }

    if (level === "3") {
      return (
        <Typography variant="body1" component="div">
          <b>Level 3: Spinning:</b> {ageGroup === "Child" ? "Parents" : "Partner"} will do spinning – {firstName} will sit in a chair and {ageGroup === "Child" ? "parent" : "partner"} will spin fast to the {program}, {direction} for <b>2 seconds per rotation</b> for <b>10 – 70 rotations</b> and repeat if needed and they can tolerate until you can see Nystagmus (beating) of the eyes when stopped.
          <div className="d-flex justify-content-around">
            <img
              src={`../../user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`}
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/ch6f6lkyy83piadn9oxzs/Melillo-Visual-Testing-Vestibular-L3.mp4?rlkey=hmd292yoc1zrfb72jwar8ptsh&st=xs8izqvs&dl=0" className="d-flex justify-content-center">Level 3 Spinning Instructions</a>
        </Typography>
      );
    }

    if (level === "4") {
      return (
        <Typography variant="body1" component="div">
          <b>Level 4:</b> {ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the {program}, {direction} for <b>2 seconds per rotation</b> for <b>10 – 50 rotations</b> and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped.
          <div className="d-flex justify-content-around">
            <img
              src={`../../user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`}
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/di8rp8i05km5x4j15f6x6/Melillo-Visual-Testing-Vestibular-L4.mp4?rlkey=ji6qjqot078exy8s27hxz4lgk&st=t1aymhzd&dl=0" className="d-flex justify-content-center">Level 4 Spinning Instructions</a>
        </Typography>
      );
    }

    if (level === "5") {
      return (
        <Typography variant="body1" component="div">
          <b>Level 5:</b> {ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the {program}, {direction} for <b>2 seconds per rotation</b> for <b>10 – 30 rotations</b> and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped for <b>7 – 10 seconds</b>.
          <div className="d-flex justify-content-around">
            <img
              src={`../../user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`}
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/esniccvejykd03dnnjzwq/Melillo-Visual-Testing-Vestibular-L5.mp4?rlkey=dcfawgjhatlt3a2k50hm6y5ki&st=2cyrwgn4&dl=0" className="d-flex justify-content-center">Level 5 Spinning Instructions</a>
        </Typography>
      );
    }

    if (level === "6") {
      return (
        <Typography variant="body1" component="div">
          <b>Level 6: Spinning:</b> {ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the {program}, {direction} for <b>2 seconds per rotation</b> for <b>10 – 20 rotations</b> and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped for <b>7 – 10 seconds</b>.
          <div className="d-flex justify-content-around">
            <img
              src={`../../user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`}
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/vb0gqzs163lnhspstmke4/Melillo-Visual-Testing-Vestibular-L6.mp4?rlkey=2i59s298ghsiwta3wx0n5vrdp&st=czwcushc&dl=0" className="d-flex justify-content-center">Level 6 Spinning Instructions</a>
        </Typography>
      );
    }

    if (level === "7") {
      return (
        <Typography variant="body1" component="div">
          <b>Level 7: Spinning:</b> {ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the {program}, {direction} for <b>2 seconds per rotation</b> for <b>10 rotations</b> and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped for <b>10 seconds</b>.
          <div className="d-flex justify-content-around">
            <img
              src={`../../user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`}
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/e6w7rwudplr28mavl9ofe/Melillo-Visual-Testing-Vestibular-L7.mp4?rlkey=qky89nas5u2fo5b0fycbs7tkk&st=hu9keluv&dl=0" className="d-flex justify-content-center">Level 7 Spinning Instructions</a>
        </Typography>
      );
    }

    if (level === "8") {
      return (
        <Typography variant="body1" component="div">
          <b>Level 8: Spinning:</b> {ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the {program}, {direction} for <b>2 seconds per rotation</b> for <b>10 rotations</b> and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped for <b>
            5 seconds</b>.
          <div className="d-flex justify-content-around">
            <img
              src={`../../user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`}
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/uzltgufmwxxnte7ygwdta/Melillo-Visual-Testing-Vestibular-L8.mp4?rlkey=sgatsrbfupltyxsjrwpnp0y4h&st=rgw2f229&dl=0" className="d-flex justify-content-center">Level 8 Spinning Instructions</a>
        </Typography>
      );
    }

    if (level === "9") {
      return (
        <Typography variant="body1" component="div">
          <b>Level 9: Spinning:</b> {ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the {program}, {direction} for <b>2 seconds per rotation</b> for <b>10 rotations</b> and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped for <b>10 seconds</b>.
          <div className="d-flex justify-content-around">
            <img
              src={`../../user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`}
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/ah9dhmglhkja7tvaxq3oh/Melillo-Visual-Testing-Vestibular-L9.mp4?rlkey=pnet7h5rbr1qxq780y8i9rlbe&st=eaoj4bht&dl=0" className="d-flex justify-content-center">Level 9 Spinning Instructions</a>
        </Typography>
      );
    }

    if (level === "10") {
      return (
        <Typography variant="body1" component="div">
          <b>Level 10:</b> {firstName} will hold hands clapped together in front of them and focus eyes on them to suppress VOR - {ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the {program}, {direction} for <b>2 seconds per rotation</b> for <b>10 rotations</b> as shown in the video instructions below.
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/vestibular/image2.jpeg"
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/zzp8yh06rwwilnm92xjv6/Melillo-Visual-Testing-Vestibular-L10.mp4?rlkey=1jhvre0g1efnuafz0h9bqno0v&st=bfhr0i4j&dl=0" className="d-flex justify-content-center">LEVEL 10 Vestibular / Spinning Instructions with Equipment</a>
        </Typography>
      );
    }

    return null;
  }

  function BalanceExercise({ level, genderAdjective }) {
    if (level === "1")
      return (
        <>
          <Typography variant="body1">
            <b>Level 1: Romberg with Eyes Open</b>: Shoes and socks off, while standing on a level surface, stand with feet together so they are touching. The goal for this level is to stand in this position, with eyes open for <b>30 seconds</b> without losing {genderAdjective} balance or wobbling before proceeding to the next level.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/be/be_1.jpeg"
              alt="exercise"
              width={180}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/a1khomjkeek4d4vame28f/Melillo-Balance-Assessment-and-Treatment-Level-1-V2.mp4?rlkey=ph7rm98xpe64411uqxvnt9rwx&st=f22d7se3&dl=0'
            className="d-flex justify-content-center">Balance Level 1 Instruction Video</a>
        </>
      );
    if (level === "2")
      return (
        <>
          <Typography variant="body1">
            <b>Level 2: Romberg with Eyes Closed</b>: Shoes and socks are off, while standing on a level surface, stand with feet together so they are touching and close eyes.  The goal for this level is to stand in this position, with eyes closed for <b>30 seconds</b> without losing balance, falling or wobbling consistently for 5 sessions in row before proceeding to the next level.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/be/be_2.jpeg"
              alt="exercise"
              width={180}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/8ato37var37yc3w0v087a/Melillo-Balance-Assessment-and-Treatment-Level-2-V2.mp4?rlkey=gtwp0xl2uozp0l65ohse7suxe&st=4yk5sfb1&dl=0'
            className="d-flex justify-content-center">Balance Level 2 Instruction Video</a>
        </>
      );
    if (level === "3")
      return (
        <>
          <Typography variant="body1">
            <b>Level 3: Mann’s Test with Eyes Open</b>: Socks and shoes are off - Stand in a tandem gait position (left foot directly in front of the right foot with the heel of the left foot touching the toe of the right foot), with eyes open. The goal for this level is to maintain this position without losing balance, falling or wobbling for <b>30 seconds</b> for 5 sessions in a row before proceeding to the next level.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/be/be_3.jpeg"
              alt="exercise"
              width={180}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/135ijfd10bu9b9eiojabt/Melillo-Balance-Assessment-and-Treatment-Level-3-V2.mp4?rlkey=nqg68iuxrhqspd97sbspdde5x&st=0mmgd26y&dl=0'
            className="d-flex justify-content-center">Balance Level 3 Instruction Video</a>
        </>
      );
    if (level === "4")
      return (
        <>
          <Typography variant="body1">
            <b>Level 4: Mann’s Test with Eyes Closed</b>: Socks and shoes are off - Stand in a tandem gait position (left foot directly in front of the right foot with the heel of the left foot touching the toe of the right foot), with eyes closed. The goal for this level is to consistently maintain this position without losing balance, falling or wobbling for <b>30 seconds</b> for 5 sessions  in a row before proceeding to the next level.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/be/be_4.jpeg"
              alt="exercise"
              width={180}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/q9mt5aike3i7qlvdr2pmh/Melillo-Balance-Assessment-and-Treatment-Level-4-V2.mp4?rlkey=5kuxpc4s649ejp6bstfluzb7i&st=2ipe2kc8&dl=0'
            className="d-flex justify-content-center">Balance Level 4 Instruction Video</a>
        </>
      );
    if (level === "5")
      return (
        <>
          <Typography variant="body1">
            <b>Level 5: One Leg Stand with Eyes Open</b>: With socks and shoes off, while standing on a level surface, stand on one leg with eyes open. Stand on just the {program === 'left' ? 'right' : 'left'} leg.  Do not hold on to anything for support or rest one leg on the other during this exercise. The goal for this level is to maintain balance for <b>30 seconds</b> without swaying, wobbling, falling or jumping consistently for 5 sessions in a row before moving to the next level.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/be/be_5.jpeg"
              alt="exercise"
              width={180}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/zlq7t3zq0fylf1rn7v8u7/Melillo-Balance-Assessment-and-Treatment-Level-5-V2.mp4?rlkey=2nauq2053hhgztg026ylq9hw4&st=cfhsrduu&dl=0'
            className="d-flex justify-content-center">Balance Level 5 Instruction Video</a>
        </>
      );
    if (level === "6")
      return (
        <>
          <Typography variant="body1">
            <b>Level 6: One Leg Stand with Eyes Closed</b>: With socks and shoes off, standing on a level surface, stand on one leg with eyes open. Stand on just the {program === 'left' ? 'right' : 'left'} leg.  Once balanced, close eyes. Do not hold on to anything for support or rest one leg on the other during this exercise. The goal for this level is to maintain balance on one leg for <b>30 seconds</b> consistently without swaying, wobbling, falling or jumping for 5 sessions in a row before proceeding to the next level.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/be/be_6.jpeg"
              alt="exercise"
              width={180}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/bnqfynjlqgdcfc5amatab/Melillo-Balance-Assessment-and-Treatment-Level-6-V2.mp4?rlkey=eh79btyzl9u657ruustiltcvo&st=3dzbb9x5&dl=0'
            className="d-flex justify-content-center">Balance Level 6 Instruction Video</a>
        </>
      );
    if (level === "7")
      return (
        <>
          <Typography variant="body1">
            <b>Level 7: Romberg with Eyes Open on Foam Pad</b>: With shoes and socks off, stand on a foam pad with {genderAdjective} feet together so they are touching and hands at side. The goal for this level is to stand in this position, hands at {genderAdjective} side, with eyes open for <b>30 seconds</b> without losing balance, swaying, falling or wobbling for 5 sessions in a row before proceeding to the next level.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/be/be_7.jpeg"
              alt="exercise"
              width={180}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/0x3g9ozdeygxkl3onrnp7/Melillo-Balance-Assessment-and-Treatment-Level-7-V2.mp4?rlkey=bt6hgj4oml0rkmx9ir86d8vnq&st=01xuawvk&dl=0'
            className="d-flex justify-content-center">Balance Level 7 Instruction Video</a>
        </>
      );
    if (level === "8")
      return (
        <>
          <Typography variant="body1">
            <b>Level 8: Romberg with Eyes Closed on Foam Pad</b>: With shoes and socks off, stand on a foam pad with their feet together so they are touching and hands at side and eyes closed. The goal for this level is to stand in this position, hands at {genderAdjective} side, with eyes closed for <b>30 seconds</b> without losing balance, swaying, falling or wobbling for 5 sessions in a row before proceeding to the next level.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/be/be_8.jpeg"
              alt="exercise"
              width={180}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/c91iwzs5jgm3k7lr8kzea/Melillo-Balance-Assessment-and-Treatment-Level-8-V2.mp4?rlkey=3wdeptrkldcwanzmvtl448gp8&st=r1pr2b5v&dl=0'
            className="d-flex justify-content-center">Balance Level 8 Instruction Video</a>
        </>
      );
    if (level === "9")
      return (
        <>
          <Typography variant="body1">
            <b>Level 9: One Leg Stand with Eyes Closed on Foam Pad</b>: With socks and shoes off, stand on a foam pad. stand on {program === 'right' ? 'left' : 'right'} leg. Once balanced, close eyes. Do not hold on to anything for support or rest one leg on the other during this exercise. The goal for this level is to maintain balance for <b>30 seconds</b> on one leg with eyes closed without wobbling, falling or jumping for 5 sessions in a row before proceeding to the next level.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/be/be_9.jpeg"
              alt="exercise"
              width={180}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/jahc3un7yho5ywghmhhqm/Melillo-Balance-Assessment-and-Treatment-Level-9-V2.mp4?rlkey=ht9i9vml3ddp0mgqvvncfui2u&st=djxvzm2h&dl=0'
            className="d-flex justify-content-center">Balance Level 9 Instruction Video</a>
        </>
      );
    if (level === "10")
      return (
        <>
          <Typography variant="body1">
            <b>Level 10: One Leg Stand with Eyes Closed on Bosu Ball</b>: With socks and shoes off, flip the Bosu Ball over so the inflated side is on the ground, and the flat plastic side is up. Stand on the flat side of the Bosu ball on one leg. Stand on just the {program === 'right' ? 'left' : 'right'} leg. Once balanced, close eyes. Do not hold on to anything for support or rest one leg on the other during this exercise. The goal for this level is to consistently maintain balance for <b>30 seconds</b> on one leg without wobbling, falling or jumping to regain balance with eyes closed.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/be/be_10.jpeg"
              alt="exercise"
              width={180}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/8cv8zrm4uk3tpq5ppbx0l/Melillo-Balance-Assessment-and-Treatment-Level-10-V2.mp4?rlkey=bxxc23ks825fpeyp1wn27k6wh&st=64fpein2&dl=0'
            className="d-flex justify-content-center">Balance Level 10 Instruction Video</a>
        </>
      );
    return null;
  }
  function CSProne({ level }) {
    if (level === "1") {
      return (
        <>
          <Typography variant="body1">
            <b>Prone - Level 1:</b> Starting on the floor and lay face down, lift each limb separately and lift head up, hold it perfectly straight for <b>15 seconds</b> for each limb.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_pro_1.jpeg"
              alt="csprone"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_pro_2.jpeg"
              alt="csprone"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/xjnc6dim1imtith7kowin/Melillo-Core-Exercises-Prone-Bridge-Level-1.mp4?rlkey=le0cfnt6npmbnkf1231a4iu6r&st=90wfp8sb&dl=0'
            className="d-flex justify-content-center">Prone Level 1 Instruction Video</a>
        </>
      );
    }
    if (level === "2") {
      return (
        <>
          <Typography variant="body1">
            <b>Prone - Level 2:</b> Starting on the floor and lay face down, lift each limb separately and lift head up, hold it perfectly straight for <b>45 seconds</b> for each limb.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_pro_3.jpeg"
              alt="csprone"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_pro_4.jpeg"
              alt="csprone"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/gt7nmg6r9odyv1x600vj1/Melillo-Core-Exercises-Prone-Bridge-Level-2.mp4?rlkey=9cev07007qmlz0jueqdci8zjo&st=35u6vhip&dl=0'
            className="d-flex justify-content-center">Prone Level 2 Instruction Video</a>
        </>
      );
    }
    if (level === "3") {
      return (
        <>
          <Typography variant="body1">
            <b>Prone - Level 3:</b> Starting on the floor and lay face down, lift up opposite arm and leg, and lift head up also. Hold it perfectly straight, switch sides and do other arm and leg. Goal is <b>15 seconds</b> for each side.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_pro_5.jpeg"
              alt="csprone"
              width={275}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/c237dkjmg7ibv0h5khb1b/Melillo-Core-Exercises-Prone-Bridge-Level-3.mp4?rlkey=pcxq2g9d5si70v6opu5x01klt&st=4p659p3p&dl=0"
            className="d-flex justify-content-center"
          >
            Prone Level 3 Instruction Video
          </a>
        </>
      );
    }

    if (level === "4") {
      return (
        <>
          <Typography variant="body1">
            <b>Prone - Level 4:</b> Starting on the floor and lay face down, lift up opposite arm and leg, and lift head up also. Hold it perfectly straight, switch sides and do other arm and leg. Goal is <b>30 seconds</b> for each side.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_pro_6.jpeg"
              alt="csprone"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/24mzo24al15ex3f04zao6/Melillo-Core-Exercises-Prone-Bridge-Level-4.mp4?rlkey=q59agqigvhv2uwn8custt6lqd&st=trjfbrf7&dl=0"
            className="d-flex justify-content-center"
          >
            Prone Level 4 Instruction Video
          </a>
        </>
      );
    }

    if (level === "5") {
      return (
        <>
          <Typography variant="body1">
            <b>Prone - Level 5:</b> Starting on the floor and lay face down, lift up opposite arm and leg, and lift head up also. Hold it perfectly straight, switch sides and do other arm and leg. Goal is <b>60 seconds</b> for each side.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_pro_7.jpeg"
              alt="csprone"
              width={300}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/bkipx8y29mtqtlct6o5i5/Melillo-Core-Exercises-Prone-Bridge-Level-5.mp4?rlkey=13qtzyuegheio40uxs9wdhfsc&st=72asdh7f&dl=0"
            className="d-flex justify-content-center"
          >
            Prone Level 5 Instruction Video
          </a>
        </>
      );
    }

    if (level === "6") {
      return (
        <>
          <Typography variant="body1">
            <b>Prone - Level 6 (Superman):</b> Lay face down, lift arms, legs and head all at the same time, hold as still and straight. Goal is <b>30 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_pro_8.jpeg"
              alt="csprone"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/a7mm2mqrsv1e1wnfmc9c6/Melillo-Core-Exercises-Prone-Bridge-Level-6.mp4?rlkey=2ztozpn3pzomkmvawof5py3y7&st=xlhmzv34&dl=0"
            className="d-flex justify-content-center"
          >
            Prone Level 6 Instruction Video
          </a>
        </>
      );
    }

    if (level === "7") {
      return (
        <>
          <Typography variant="body1">
            <b>Prone - Level 7 (Superman):</b> Lay face down, lift arms, legs and head all at the same time, hold as still and straight. Goal is <b>60 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_pro_9.jpeg"
              alt="csprone"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/odlufsym1te79u3uezr83/Melillo-Core-Exercises-Prone-Bridge-Level-7.mp4?rlkey=irc51e82l3hl05pivcn4dpojj&st=se12k0bh&dl=0"
            className="d-flex justify-content-center"
          >
            Prone Level 7 Instruction Video
          </a>
        </>
      );
    }

    if (level === "8") {
      return (
        <>
          <Typography variant="body1">
            <b>Prone - Level 8 (Sorenson Hold):</b> Laying on a bench or a bed, {ageGroup === "Child" ? `parent` : `partner`} will hold feet stable and {firstName} will lift up to extend back with arms crossed over chest and hold for <b>1 minute</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_pro_10.jpeg"
              alt="csprone"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/yn33sqr3lz1whu9mac588/Melillo-Core-Exercises-Prone-Bridge-Level-8.mp4?rlkey=2w32cqo66c6jal1gmxzviqkfh&st=im4qmq7o&dl=0"
            className="d-flex justify-content-center"
          >
            Prone Level 8 Instruction Video
          </a>
        </>
      );
    }

    if (level === "9") {
      return (
        <>
          <Typography variant="body1">
            <b>Prone - Level 9 (Sorenson Hold):</b> Laying on a bench or a bed, {ageGroup === "Child" ? `parent` : `partner`} will hold feet stable and {firstName} will lift up to extend back with arms crossed over chest and hold for <b>2 minutes</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_pro_11.jpeg"
              alt="csprone"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/t9e6vgk8dxk2ijunyllsq/Melillo-Core-Exercises-Prone-Bridge-Level-9.mp4?rlkey=kj4q9qffb4hfpeennvbihjhb9&st=rzoggxbj&dl=0"
            className="d-flex justify-content-center"
          >
            Prone Level 9 Instruction Video
          </a>
        </>
      );
    }

    if (level === "10") {
      return (
        <>
          <Typography variant="body1">
            <b>Prone - Level 10 (Weighted Sorenson Hold):</b> Prior to starting, put on a weighted vest. Laying on a bench or a bed, {ageGroup === "Child" ? `parent` : `partner`} will hold feet stable, and {firstName} will lift up to extend back with arms crossed over chest and hold for <b>1 minute</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_pro_12.jpeg"
              alt="csprone"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/i2pcf968u9qqneoe79uu7/Melillo-Core-Exercises-Prone-Bridge-Level-10-V3.mp4?rlkey=ct5wqieznfvotdsmgroq8v1g6&st=lj1wapub&dl=0"
            className="d-flex justify-content-center"
          >
            Prone Level 10 Instruction Video
          </a>
        </>
      );

    }
    return null;
  }

  function CSLateral({ level }) {
    if (level === "1") {
      return (
        <>
          <Typography variant="body1">
            <b>Lateral - Level 1:</b> Laying on the side, legs and body should be straight. Both hands on the floor along with bottom arm and elbow. Both feet should be on the ground. Raise the top leg and hold in a stable position. Reverse and do other side. Goal is <b>30 seconds</b> each side.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_lat_1.jpeg"
              alt="cslateral"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/jxatk86jrzs8fnhx0nfr4/Melillo-Core-Exercises-Lateral-Side-Plank-Level-1.mp4?rlkey=yjjqdlhmw4cgkevn1ymfevmh1&st=1vpntyrp&dl=0"
            className="d-flex justify-content-center"
          >
            Lateral: Level 1 Instruction Video
          </a>
        </>
      );
    }
    if (level === "2") {
      return (
        <>
          <Typography variant="body1">
            <b>Lateral - Level 2:</b> Side plank with bent knees – Both hands on floor along with bottom arm and elbow, knees are bent and lift hips off the ground and hold in a stable position for <b>30 seconds</b> and then switch sides.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_lat_2.jpeg"
              alt="cslateral"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/iquvc2yiy3roycdvurhyx/Melillo-Core-Exercises-Lateral-Side-Plank-Level-2.mp4?rlkey=3iyajiee3vytdpxv3wdf5t0jc&st=tltf67r6&dl=0"
            className="d-flex justify-content-center"
          >
            Lateral: Level 2 Instruction Video
          </a>
        </>
      );
    }
    if (level === "3") {
      return (
        <>
          <Typography variant="body1">
            <b>Lateral - Level 3:</b> Legs and body should be straight. Both hands are on the floor along with the bottom arm and elbow. Feet at this level will be with the outside of bottom foot on the floor and inside of top foot also on the floor. Raise hips off the ground to make a lateral bridge, hold in a stable position, reverse and do other side. Goal is <b>30 seconds</b> on each side.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_lat_3.jpeg"
              alt="cslateral"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/n48ndaytzhbecobp438z2/Melillo-Core-Exercises-Lateral-Side-Plank-Level-3.mp4?rlkey=8z9yajcrmddjxvdfe5jet69yg&st=1zyq35ke&dl=0"
            className="d-flex justify-content-center"
          >
            Lateral: Level 3 Instruction Video
          </a>
        </>
      );
    }
    if (level === "4") {
      return (
        <>
          <Typography variant="body1">
            <b>Lateral - Level 4:</b> Legs and body should be straight. Both hands are on the floor along with the bottom arm and elbow. Feet at this level will be stacked. Raise hips off the ground for a lateral bridge… Hold as long as possible. Reverse and do other side. Goal is <b>60 seconds</b> each side.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_lat_4.jpeg"
              alt="cslateral"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/sl319je3dntt234d5oqgi/Melillo-Core-Exercises-Lateral-Side-Plank-Level-4.mp4?rlkey=80ej5fq8ccem11vvjygtyqk7n&st=z693ygqd&dl=0"
            className="d-flex justify-content-center"
          >
            Lateral: Level 4 Instruction Video
          </a>
        </>
      );
    }
    if (level === "5") {
      return (
        <>
          <Typography variant="body1">
            <b>Lateral - Level 5:</b> Weighted side plank with a weighted vest on: Legs and body should be straight. Both hands are on the floor along with the bottom arm and elbow. Feet at this level will be stacked. Raise hips off the ground for a lateral bridge… Hold as long as possible. Reverse and do other side. Goal is <b>60 seconds</b> each side.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_lat_5.jpeg"
              alt="cslateral"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/bzjtwmbm6k6lehz5xgztt/Melillo-Core-Exercises-Lateral-Side-Plank-Level-5-V3.mp4?rlkey=645t5vgc6bjq93qripbisgz3l&st=xlkf41me&dl=0"
            className="d-flex justify-content-center"
          >
            Lateral: Level 5 Instruction Video
          </a>
        </>
      );
    }
    if (level === "6") {
      return (
        <>
          <Typography variant="body1">
            <b>Lateral - Level 6:</b> On side, hands on floor along with bottom arm and elbow, feet will start off by being stacked. Then lift the hips and the top leg – the goal is for <b>30 seconds</b>. Then switch sides and do with opposite leg.
          </Typography>
          <div class="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_lat_6.jpeg"
              alt="cslateral"
              width={275}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/njfsbj2y3xr555o99rta8/Melillo-Core-Exercises-Lateral-Side-Plank-Level-6.mp4?rlkey=h38xf7lheaelomcrxl1y6b9gs&st=9eyb0cqd&dl=0"
            className="d-flex justify-content-center"
          >
            Lateral: Level 6 Instruction Video
          </a>
        </>
      );
    }
    if (level === "7") {
      return (
        <>
          <Typography variant="body1">
            <b>Lateral - Level 7:</b> On side, hands on floor along with bottom arm and elbow, feet will start off by being stacked. Then lift the hips, the top arm and the top leg – the goal is for <b>30 seconds</b>. Then switch sides and do with opposite side.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_lat_7.jpeg"
              alt="cslateral"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/r1r0cuei4x731i7w3prwc/Melillo-Core-Exercises-Lateral-Side-Plank-Level-7.mp4?rlkey=fgasdb536dg5qbuao6d5lbtn9&st=sgj8mf75&dl=0"
            className="d-flex justify-content-center"
          >
            Lateral: Level 7 Instruction Video
          </a>
        </>
      );
    }
    if (level === "8") {
      return (
        <>
          <Typography variant="body1">
            <b>Lateral - Level 8:</b> Laying on side with bottom arm extended, lift up and top arm and top leg also extends and hold the position stable for <b>30 seconds</b>, then switch sides.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_lat_8.jpeg"
              alt="cslateral"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/ckjazch7x1cy0yp3xmtxo/Melillo-Core-Exercises-Lateral-Side-Plank-Level-8.mp4?rlkey=n9wn2pse94lnujxu08k4c4bpr&st=ezkuttpp&dl=0"
            className="d-flex justify-content-center"
          >
            Lateral: Level 8 Instruction Video
          </a>
        </>
      );
    }
    if (level === "9") {
      return (
        <>
          <Typography variant="body1">
            <b>Lateral - Level 9:</b> Laying on side with bottom arm extended, lift up and top arm and top leg also extends and hold the position stable for <b>60 seconds</b>, then switch sides.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_lat_9.jpeg"
              alt="cslateral"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/bktvcs40ffllncwjurasw/Melillo-Core-Exercises-Lateral-Side-Plank-Level-9.mp4?rlkey=1sfawek4isvv3e8n5rmtghrei&st=rre9hlyr&dl=0"
            className="d-flex justify-content-center"
          >
            Lateral: Level 9 Instruction Video
          </a>
        </>
      );
    }
    if (level === "10") {
      return (
        <>
          <Typography variant="body1">
            <b>Lateral - Level 10:</b> Weighted Starfish Plank: Prior to starting, put on a weighted vest. Start in the side plank position. From here, come up onto the hand that is closest to the ground. From here, raise top arm straight up to the ceiling and raise the top leg up to make a starfish with their body. Make sure to do whichever side is more challenging first, then match it on the other side to ensure it does not create a muscular imbalance.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_lat_10.jpeg"
              alt="cslateral"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/wws9igm2d7ch4rit917wr/Melillo-Core-Exercises-Lateral-Side-Plank-Level-10-V3.mp4?rlkey=djlbdj9a5nug0jqr2qw3g3qry&st=7vps1k31&dl=0"
            className="d-flex justify-content-center"
          >
            Lateral: Level 10 Instruction Video
          </a>
        </>
      );
    }
    return null;
  }

  function CSSupine({ level }) {
    if (level === "1") {
      return (
        <>
          <Typography variant="body1">
            <b>Supine - Level 1:</b> On the floor face up, feet and arms flat on the floor and knees should be shoulder width apart & arms at the side. Raise back off the floor, hold as long as possible. Goal is <b>30 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_sup_1.jpeg"
              alt="cssupine"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/opm6ygcqx07yfav7wcnsp/Melillo-Core-Exercises-Supine-Bridge-Level-1.mp4?rlkey=be862qhbgx56r570vhcex5sr2&st=2pv7a7br&dl=0"
            className="d-flex justify-content-center">Supine: Level 1 Instruction Video</a>
        </>
      );
    }
    if (level === "2") {
      return (
        <>
          <Typography variant="body1">
            <b>Supine - Level 2:</b> On the floor face up, feet and arms flat on the floor and knees should be shoulder width apart & arms at side. Raise back off the floor. Goal is <b>60 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_sup_2.jpeg"
              alt="cssupine"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/o71cimxoivpn3g0msft9o/Melillo-Core-Exercises-Supine-Bridge-Level-2.mp4?rlkey=tcl3i9mxmwml68f2c52k2frfn&st=40yuro8u&dl=0"
            className="d-flex justify-content-center">Supine: Level 2 Instruction Video</a>
        </>
      );
    }
    if (level === "3") {
      return (
        <>
          <Typography variant="body1">
            <b>Supine - Level 3:</b> Starting position is on the floor, laying face up, feet are together, knees are together, and arms are crossed. Raise back off the floor, hold this position. Goal is <b>30 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_sup_3.jpeg"
              alt="cssupine"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/tc67hqmbklkbcibbp6xn9/Melillo-Core-Exercises-Supine-Bridge-Level-3.mp4?rlkey=g28wo7m43iyq9r868f5z9shu7&st=mcdpr9n4&dl=0"
            className="d-flex justify-content-center">Supine: Level 3 Instruction Video</a>
        </>
      );
    }
    if (level === "4") {
      return (
        <>
          <Typography variant="body1">
            <b>Supine - Level 4:</b> Starting position is on the floor, laying face up, feet are together, knees are together, and arms are crossed. Raise back off the floor, hold this position. Goal is <b>60 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_sup_4.jpeg"
              alt="cssupine"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/pb1ueo252d1nqh917r6lu/Melillo-Core-Exercises-Supine-Bridge-Level-4.mp4?rlkey=m47yt7zxk1kuzu3jv97hm64as&st=0cftiols&dl=0"
            className="d-flex justify-content-center">Supine: Level 4 Instruction Video</a>
        </>
      );
    }
    if (level === "5") {
      return (
        <>
          <Typography variant="body1">
            <b>Supine - Level 5:</b> Arms at the side, flat on the floor, lift leg (one at a time) and bottom off the ground and hold for <b>30 seconds</b>. Then switch and do on the other side with the other leg.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_sup_5.jpeg"
              alt="cssupine"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/7tffsypmvbx6c4vroane7/Melillo-Core-Exercises-Supine-Bridge-Level-5.mp4?rlkey=660jf9wg94nqqa156u8e23rvd&st=ltyslvhe&dl=0"
            className="d-flex justify-content-center">Supine: Level 5 Instruction Video</a>
        </>
      );
    }
    if (level === "6") {
      return (
        <>
          <Typography variant="body1">
            <b>Supine - Level 6:</b> Arms crossed over chest, lift leg (one at a time) and bottom off the ground and hold for <b>60 seconds</b>. Then switch and do on the other side with the other leg.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_sup_6.jpeg"
              alt="cssupine"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/eira6rd6tqm1pzbuvkoq3/Melillo-Core-Exercises-Supine-Bridge-Level-6.mp4?rlkey=sddou3e3r6vnuwasn0t3rop7j&st=4d0kky24&dl=0"
            className="d-flex justify-content-center">Supine: Level 6 Instruction Video</a>
        </>
      );
    }
    if (level === "7") {
      return (
        <>
          <Typography variant="body1">
            <b>Supine - Level 7:</b> Laying on the back, arms crossed over chest and feet are on a bosa ball or a box. Lift off the ground to form a bridge and hold for <b>60 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_sup_7.jpeg"
              alt="cssupine"
              width={275}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/0bevpd9fmnxzqisn3zhyc/Melillo-Core-Exercises-Supine-Bridge-Level-7.mp4?rlkey=4jlu8hrhv9uwjtg3fd8hx7bks&st=1h7dp0vf&dl=0"
            className="d-flex justify-content-center">Supine: Level 7 Instruction Video</a>
        </>
      );
    }
    if (level === "8") {
      return (
        <>
          <Typography variant="body1">
            <b>Supine - Level 8:</b> Laying on the back, arms crossed over chest and feet are on a bosa ball or a box. Lift off the ground to form a bridge & lift leg, hold for 60 seconds & switch to the other leg and hold for <b>60 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_sup_8.jpeg"
              alt="cssupine"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/gza4wrx5wq94zf9zhrra9/Melillo-Core-Exercises-Supine-Bridge-Level-8.mp4?rlkey=5xua04av8lnltkxg6wcszeqh4&st=tv9bmxbz&dl=0"
            className="d-flex justify-content-center">Supine: Level 8 Instruction Video</a>
        </>
      );
    }
    if (level === "9") {
      return (
        <>
          <Typography variant="body1">
            <b>Supine Bridge with Weighted Vest- Level 9:</b> Laying on back, put a weighted vest on the hip crease/ belt line, then lift up into bridge position and hold for <b>60 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_sup_9.jpeg"
              alt="cssupine"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/kfynjvd7d27dy7fau5ea9/Melillo-Core-Exercises-Supine-Bridge-Level-9-V3.mp4?rlkey=w6zhwp29urbzzni49wtkjmyuy&st=bit76s7l&dl=0"
            className="d-flex justify-content-center">Supine: Level 9 Instruction Video</a>
        </>
      );
    }
    if (level === "10") {
      return (
        <>
          <Typography variant="body1">
            <b>Supine Bridge with Weight Vest - Level 10:</b> Laying on back, put a weighted vest on the hip crease/ belt line, then lift up into bridge position, then lift each leg, one at a time and hold for <b>60 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/core/core_sup_10.jpeg"
              alt="cssupine"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/sbxlsm1qbid2k567y29tp/Melillo-Core-Exercises-Supine-Bridge-Level-10-V3.mp4?rlkey=4jmbs9qu86wd05rnsy8otdwif&st=hcpvwm9k&dl=0"
            className="d-flex justify-content-center">Supine: Level 10 Instruction Video</a>
        </>
      );
    }
    return null;
  }

  function CSCurlUps({ level }) {
    if (level === "1") {
      return (
        <>
          <Typography variant="body1">
            <b>Curl Ups with Hands Pull Assist- Level 1:</b> Laying down and {ageGroup === "Child" ? "parent" : "partner"} can sit on legs and child will hold onto {ageGroup === "Child" ? "parent" : "partner"}’s thumbs and {ageGroup === "Child" ? "parent" : "partner"} can assist in pulling them up to help do a full sit up. <b>Goal is 30 at a time</b> as they grow stronger.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_1.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/anfqe5c8h53lqbi9kkali/Melillo-Core-Exercises-Curl-Ups-Level-1-V2.mp4?rlkey=2xslfkintmr3tgkea76vhvto7&st=j8rlwr0m&dl=0"
            className="d-flex justify-content-center">Curl Ups: Level 1 Instructional Video</a>
        </>
      );
    }
    if (level === "2") {
      return (
        <>
          <Typography variant="body1">
            <b>Curl Ups - Level 2:</b> Laying down and {ageGroup === "Child" ? "parent" : "partner"} can sit on legs and child will hold onto {ageGroup === "Child" ? "parent" : "partner"}s’ thumbs and {ageGroup === "Child" ? "parent" : "partner"} can assist in pulling them up to help do a full sit up. <b>Goal is 50 at a time</b> to grow stronger and build core strength.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_2.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/anf4jf5qwmn76muxlmp59/Melillo-Core-Exercises-Curl-Ups-Level-2.mp4?rlkey=u3oo4u0np77c5psy7rwzjxzzd&st=qj4zlrg0&dl=0"
            className="d-flex justify-content-center">Curl Ups: Level 2 Instructional Video</a>
        </>
      );
    }
    if (level === "3") {
      return (
        <>
          <Typography variant="body1">
            <b>Curl Ups - Level 3 (Hands Pulling with Arms Crossed):</b> Start off with them lying on the back, arms crossed, and knees bent, bring the torso up and touch the arms to the knees and {ageGroup === "Child" ? "parent" : "partner"} can assist by helping pull up lightly with the arms, and with control back down to the floor. The goal will be to successfully complete <b>30 repetitions for 3-5 consecutive sessions</b> then we can proceed to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_3.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/ircx6fhwpue7cu6lrsugh/Melillo-Core-Exercises-Curl-Ups-Level-3.mp4?rlkey=jdhvl6v48snu4ztyatw18ycp1&st=yhi54ups&dl=0"
            className="d-flex justify-content-center">Curl Ups: Level 3 Instructional Video</a>
        </>
      );
    }
    if (level === "4") {
      return (
        <>
          <Typography variant="body1">
            <b>Curl Ups - Level 4:</b> Laying on back, arms are crossed over chest and {ageGroup === "Child" ? "parent" : "partner"} can assist in holding feet for stability. Proceed to do a full sit up and back down for <b>20 times</b> consistently.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_4.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_5.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/5klc9l7my0kyuose0kz0e/Melillo-Core-Exercises-Curl-Ups-Level-4.mp4?rlkey=mpgvd1xdtn1zau7upxy2x2pdf&st=nvy8ghiw&dl=0"
            className="d-flex justify-content-center">Curl Ups: Level 4 Instructional Video</a>
        </>
      );
    }
    if (level === "5") {
      return (
        <>
          <Typography variant="body1">
            <b>Curl Ups - Level 5:</b> Laying on back, arms are crossed over chest and {ageGroup === "Child" ? "parent" : "partner"} can assist in holding feet for stability. Proceed to do a full sit up and back down for <b>40 times</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_6.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_7.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/vwdaxnmyxo79v9iubr2rg/Melillo-Core-Exercises-Curl-Ups-Level-5.mp4?rlkey=xwfesq8dbysxt462b9uz5qcyv&st=m93ccwip&dl=0"
            className="d-flex justify-content-center">Curl Ups: Level 5 Instructional Video</a>
        </>
      );
    }
    if (level === "6") {
      return (
        <>
          <Typography variant="body1">
            <b>Curl Ups - Level 6 (Russian Twist):</b> Start in the Sit Up Iso Hold Position, with hands clasped together, rotate through the trunk from side to side. Make sure the hands touch the floor for every rep. The goal is <b>20 reps</b> for each side consistently before moving onto the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_8.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_9.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/h0m4xrv48xnyv8tugrdy1/Melillo-Core-Exercises-Curl-Ups-Level-6.mp4?rlkey=mcg6ph7iu77oe60plmafrqp71&st=g73wh2ds&dl=0"
            className="d-flex justify-content-center">Curl Ups: Level 6 Instructional Video</a>
        </>
      );
    }
    if (level === "7") {
      return (
        <>
          <Typography variant="body1">
            <b>Curl Ups - Level 7 (45 Degree Iso Hold 30 seconds):</b> Start in the bottom of the sit up position: With arms crossed, have sit all the way up until the arms touch the knees. From there, slowly lower back to the halfway position (about 45 degrees). Once here, maintain the position for as long possible or until the <b>30 seconds</b> is up.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_10.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/jdq3dr58tp4htf78tbg9w/Melillo-Core-Exercises-Curl-Ups-Level-7.mp4?rlkey=acwbhbji3qhbly85ngs7m2km8&st=xsj171hr&dl=0"
            className="d-flex justify-content-center">Curl Ups: Level 7 Instructional Video</a>
        </>
      );
    }
    if (level === "8") {
      return (
        <>
          <Typography variant="body1">
            <b>Curl Ups - Level 8 (45 Degree Iso Hold 60 seconds):</b> Start in the bottom of the sit up position: With arms crossed, have sit all the way up until the arms touch the knees. From there, slowly lower back to the halfway position (about 45 degrees). Once here, maintain the position for as long possible or until the <b>60 seconds</b> is up.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_11.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/z4n5yx4a5yseqbp35mdst/Melillo-Core-Exercises-Curl-Ups-Level-8.mp4?rlkey=dslp24uh860hmq4bw30jhyp8i&st=yz32l4fp&dl=0"
            className="d-flex justify-content-center">Curl Ups: Level 8 Instructional Video</a>
        </>
      );
    }
    if (level === "9") {
      return (
        <>
          <Typography variant="body1">
            <b>Curl Ups - Level 9 (45 Degree Iso Hold 2 minutes):</b> Start in the bottom of the sit up position: With arms crossed, sit all the way up until the arms touch the knees. From there, slowly lower back to the halfway position (about 45 degrees). Once here, maintain the position for as long as they possible or until the <b>2 minutes</b> is up.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_12.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/8hrindao2y4u3mlfg1iz2/Melillo-Core-Exercises-Curl-Ups-Level-9.mp4?rlkey=84bfbjr2dlpud8rxqggode4fn&st=a7wchl5h&dl=0"
            className="d-flex justify-content-center">Curl Ups: Level 9 Instructional Video</a>
        </>
      );
    }
    if (level === "10") {
      return (
        <>
          <Typography variant="body1">
            <b>Curl Ups - Level 10 (Weighted Iso Hold 1 minute):</b> While wearing a weighted vest, start in the bottom of the sit up position. With arms crossed, sit all the way up until the arms touch the knees. From there, slowly lower back to the halfway position (about 45 degrees). Once here, maintain the position for as long as possible or until the 1 minute is up. Once you can successfully complete <b>1 minute without compensation for 5 consecutive sessions</b>, increase the weight of the vest.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/cu/cu_13.jpeg"
              alt="cscurlups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/7q490xsscfdnjmmahsd8z/Melillo-Core-Exercises-Curl-Ups-Level-10-V3.mp4?rlkey=un027hiwaywp0gum21nrm6rqk&st=6jadjecj&dl=0"
            className="d-flex justify-content-center">Curl Ups: Level 10 Instructional Video</a>
        </>
      );
    }
    return null;
  }

  function CSPushUps({ level }) {
    if (level === "1") {
      return (
        <>
          <Typography variant="body1">
            <b>Push Ups - Level 1:</b> Wheelbarrow Walk supported - {ageGroup === "Child" ? "Parent" : "Partner"} will hold on ankles and lift legs off the ground so there is a straight line in the body. Support the weight of the legs/hips as needed by moving hands closer to the thighs, knees or hips for more support. The goal will be to walk for <b>20 steps forward</b> on the hands.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_1.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_2.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/ksh98c0hho9on8u32jbab/Melillo-Core-Exercises-Push-Ups-Level-1-V2.mp4?rlkey=4fm9tpuecs09y1hta3yczmct3&st=xboerg8r&dl=0"
            className="d-flex justify-content-center">Push Ups: Level 1 Instruction Video</a>
        </>
      );
    }
    if (level === "2") {
      return (
        <>
          <Typography variant="body1">
            <b>Push Ups - Level 2:</b> Wheelbarrow walk – {ageGroup === "Child" ? "Parent" : "Partner"} will hold on ankles and lift legs off the ground and {firstName} will walk for <b>20 steps forward and 20 steps backwards</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_3.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_4.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/vpnoipkf130j0t4kvc5xz/Melillo-Core-Exercises-Push-Ups-Level-2.mp4?rlkey=mjaln09s86eu9hxthss3t2792&st=awq81oaq&dl=0"
            className="d-flex justify-content-center">Push Ups: Level 2 Instruction Video</a>
        </>
      );
    }
    if (level === "3") {
      return (
        <>
          <Typography variant="body1">
            <b>Push Ups - Level 3:</b> Wheelbarrow Plank – {ageGroup === "Child" ? "Parent" : "Partner"} will hold feet up off the ground while {firstName} is supporting weight on {genderAdjective} arms. The goal is to hold the position for <b>60 seconds</b> stable without falling.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_5.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/1w1z5kc4otllxd7fhil7c/Melillo-Core-Exercises-Push-Ups-Level-3-V2.mp4?rlkey=gh3dx348ch0rcy0kx587nzo5l&st=kdloiq36&dl=0"
            className="d-flex justify-content-center">Push Ups: Level 3 Instruction Video</a>
        </>
      );
    }
    if (level === "4") {
      return (
        <>
          <Typography variant="body1">
            <b>Push Ups - Level 4:</b> Plank in Push Up Position – Hands directly below shoulders and back is straight and hold as long as possible until {genderSubjectPronoun} becomes unstable daily and tracking the time. Goal will be to hold in {genderAdjective} position for <b>60 seconds</b> maintaining a straight line from head to toe without shaking.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_6.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/alv1g2gun41k6ttazab8h/Melillo-Core-Exercises-Push-Ups-Level-4.mp4?rlkey=k2b34281xjumtja4dao6njaak&st=42dygbe2&dl=0"
            className="d-flex justify-content-center">Push Ups: Level 4 Instruction Video</a>
        </>
      );
    }
    if (level === "5") {
      return (
        <>
          <Typography variant="body1">
            <b>Push Ups - Level 5:</b> With a box set up in the 20” position, place the hands on the box and the feet on the ground 2-3 steps behind the box. Maintain a straight line in the body from head to toe, from here lower the chest to the box, and then push back up. The goal will be <b>15 repetitions without compensation for 5 consecutive sessions</b>, proceed to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_7.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_8.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/q2fd88vmhtmv95axqbzl4/Melillo-Core-Exercises-Push-Ups-Level-5-V3.mp4?rlkey=hxcsnzk5zbtqjhbsn28srnd0j&st=1zf0e2py&dl=0"
            className="d-flex justify-content-center">Push Ups: Level 5 Instruction Video</a>
        </>
      );
    }
    if (level === "6") {
      return (
        <>
          <Typography variant="body1">
            <b>Push Ups - Level 6:</b> Starting in the push up position, bring the knees down to the floor. Maintain a straight line in the body from head to toe. From here lower to touch the chest to the ground, and then press back up to the starting position. The goal will be to successfully complete <b>15 repetitions without compensation for 5 consecutive sessions</b>, proceed to the next.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_9.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_10.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/2uq30glntepymulbbh0ig/Melillo-Core-Exercises-Push-Ups-Level-6-V2.mp4?rlkey=ojjufxy2henjj7i5otomaeqf2&st=dwcwfflo&dl=0"
            className="d-flex justify-content-center">Push Ups: Level 6 Instruction Video</a>
        </>
      );
    }
    if (level === "7") {
      return (
        <>
          <Typography variant="body1">
            <b>Push Ups - Level 7:</b> Hands directly below shoulders and back is straight, lower down to do a full push up. Goal is <b>15 times(without stopping) for 5 sessions</b> for this level before moving to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_11.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_12.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/k56hp4ugbf8syn7jtv12c/Melillo-Core-Exercises-Push-Ups-Level-7.mp4?rlkey=92n0gpnebi6houb0xtg5vx50i&st=n43rbldq&dl=0"
            className="d-flex justify-content-center">Push Ups: Level 7 Instruction Video</a>
        </>
      );
    }
    if (level === "8") {
      return (
        <>
          <Typography variant="body1">
            <b>Push Ups - Level 8:</b> Hands directly below shoulders and back is straight, lower down to do a full push up. Goal is <b>25 times</b> for this level before moving to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_13.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_14.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/gzeeg9u9v8xyiu0l9aerf/Melillo-Core-Exercises-Push-Ups-Level-8.mp4?rlkey=tvi3840nb1azb6ut8k32t3r4e&st=09ems4j0&dl=0"
            className="d-flex justify-content-center">Push Ups: Level 8 Instruction Video</a>
        </>
      );
    }
    if (level === "9") {
      return (
        <>
          <Typography variant="body1">
            <b>Push Ups - Level 9:</b> Hands directly below shoulders and back is straight, lower down to do a full push up. Goal is <b>{genderSubjectPronoun === "she" ? `30 times` : `40 times`}</b> for this level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_15.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_16.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/avbi0oyoygjpmn59wyi2h/Melillo-Core-Exercises-Push-Ups-Level-9.mp4?rlkey=gvqjzzqb8rgfupang65d2an4p&st=cvpuwg2h&dl=0"
            className="d-flex justify-content-center">Push Ups: Level 9 Instruction Video</a>
        </>
      );
    }
    if (level === "10") {
      return (
        <>
          <Typography variant="body1">
            <b>Push Ups - Level 10:</b> Weighted Push ups: Put on a weighted vest to get started. From the top of the Push-Up position, lower the chest until it touches the floor and then press back up to the starting position. The goal is to successfully complete <b>40+ repetitions without compensation for 5 consecutive sessions</b>, increase the weight of the vest.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_17.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/psh/psh_18.jpeg"
              alt="cspushups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/3g3s8o4h9h3m6ecicqlyw/Melillo-Core-Exercises-Push-Ups-Level-10.mp4?rlkey=2c5b5ytae78l5lseyl4a6vxts&st=sa76xu69&dl=0"
            className="d-flex justify-content-center">Push Ups: Level 10 Instruction Video</a>
        </>
      );
    }
    return null;
  }

  function CSPullUps({ level }) {
    if (level === "1") {
      return (
        <>
          <Typography variant="body1">
            <b>Pull Ups - Level 1 (Supported Bar Hang):</b> Recommend {ageGroup === "Child" ? "the family" : "you"} purchase a doorway chin up bar for the home program. From the standing position, {ageGroup === "Child" ? "help them" : `${genderSubjectPronoun} will`} reach up and grab the bar with both hands. With arms straight, lift the legs off the ground, maintaining a straight line in the body, and hold that position for <b>10 seconds</b>. Support part of {genderAdjective} body weight by holding the legs.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_1.jpeg"
              alt="cspullups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/e89nqomvz6vwcnbr7e8hy/Melillo-Core-Exercises-Pull-Ups-Level-1-V2.mp4?rlkey=sulvmu4iiu4umxp9sjtz2irkx&st=0s60zni5&dl=0"
            className="d-flex justify-content-center">Pull Ups: Level 1 Instruction Video</a>
        </>
      );
    }
    if (level === "2") {
      return (
        <>
          <Typography variant="body1">
            <b>Pull Ups - Level 2 (Supported Bar Hang):</b> From the standing position, {ageGroup === "Child" ? "help them" : `${genderSubjectPronoun} will`} reach up and grab the bar with both hands. With arms straight, lift the legs off the ground, maintaining a straight line in the body, and hold that position for <b>10 seconds</b>. Support part of the body weight by holding the legs. Once {genderSubjectPronoun} can successfully complete <b>10 seconds without compensation for 5 consecutive sessions</b>, proceed to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_2.jpeg"
              alt="cspullups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/znflkq72k5dgkn2nuamd5/Melillo-Core-Exercises-Pull-Ups-Level-2.mp4?rlkey=7a8gswuc6up9ofomvv93qegpp&st=t1smx7l3&dl=0"
            className="d-flex justify-content-center">Pull Ups: Level 2 Instruction Video</a>
        </>
      );
    }
    if (level === "3") {
      return (
        <>
          <Typography variant="body1">
            <b>Pull Ups - Level 3 (Unsupported Bar Hang):</b> From the standing position, {ageGroup === "Child" ? "help them" : `${genderSubjectPronoun} will`} reach up and grab the bar with both hands. With arms straight, lift the legs off the ground, maintaining a straight line in the body, and hold that position for <b>15 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_3.jpeg"
              alt="cspullups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/w3wx837zsdpyh6mj47tha/Melillo-Core-Exercises-Pull-Ups-Level-3.mp4?rlkey=txtxg1wehddy4cyfk63j3wmg0&st=94vi0i6f&dl=0"
            className="d-flex justify-content-center">Pull Ups: Level 3 Instruction Video</a>
        </>
      );
    }
    if (level === "4") {
      return (
        <>
          <Typography variant="body1">
            <b>Pull Ups - Level 4 (Unsupported Bar Hang):</b> From the standing position, {ageGroup === "Child" ? "help them" : `${genderSubjectPronoun} will`} reach up and grab the bar with both hands. With arms straight, lift the legs off the ground, maintaining a straight line in the body, and hold that position for <b>30 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_4.jpeg"
              alt="cspullups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/pd8bxmhlqak0w3lriqwh5/Melillo-Core-Exercises-Pull-Ups-Level-4.mp4?rlkey=e689i8x3gqpn8ikbsouz6vbdw&st=335cas6k&dl=0"
            className="d-flex justify-content-center">Pull Ups: Level 4 Instruction Video</a>
        </>
      );
    }
    if (level === "5") {
      return (
        <>
          <Typography variant="body1">
            <b>Pull Ups - Level 5 (Unsupported Bar Hang):</b> From the standing position, {ageGroup === "Child" ? "help them" : `${genderSubjectPronoun} will`} reach up and grab the bar with both hands. With arms straight, lift the legs off the ground, maintaining a straight line in the body, and hold that position for <b>60 seconds</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_5.jpeg"
              alt="cspullups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/23q8rpfy2znrfudtgybt3/Melillo-Core-Exercises-Pull-Ups-Level-5.mp4?rlkey=iyrywtjk0qjq9nerai905vfbg&st=6afnuh5a&dl=0"
            className="d-flex justify-content-center">Pull Ups: Level 5 Instruction Video</a>
        </>
      );
    }
    if (level === "6") {
      return (
        <>
          <Typography variant="body1">
            <b>Pull Ups - Level 6 (45 Degree Ring Row):</b> Use home stretch wraps or ring - set up rings so they are at the hip height level. Stand with feet directly under the pull up bar and pull the rings tightly to the chest. With {ageGroup === "Child" ? `parent's` : `partner's`} support, lower the body down until their arms are fully extended and body is at a <b>45 degree angle</b>. Maintain a straight line with the body from head to toe. Pull the body with hands in pronated position until the rings touch the chest, then lower back down with control. The goal is to successfully complete <b>15 reps without compensation for 5 consecutive sessions</b>, proceed to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_6.jpeg"
              alt="cspullups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_7.jpeg"
              alt="cspullups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/x2px49qsg403wbb5azqpa/Melillo-Core-Exercises-Pull-Ups-Level-6-V2.mp4?rlkey=wokjl73kgku9vknirimla9rgz&st=p1pfcdwh&dl=0"
            className="d-flex justify-content-center">Pull Ups: Level 6 Instruction Video</a>
        </>
      );
    }
    if (level === "7") {
      return (
        <>
          <Typography variant="body1">
            <b>Pull Ups - Level 7 (Flexed Arm Hang, 30 Sec):</b> Start by grabbing the bar and jumping or pulling up so the arms are fully flexed and the chin is above the bar. Maintain this position and hang for <b>30 seconds</b>. Be sure not to rest their chin on the bar. The goal for this level is to successfully hang for <b>30 seconds without compensation for 3-5 consecutive sessions</b>, proceed to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_8.jpeg"
              alt="cspullups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/9f10s2vx5g7183c30gk2j/Melillo-Core-Exercises-Pull-Ups-Level-7.mp4?rlkey=q8ob4z5l22qyixqaxjn0w0hh2&st=sa911yrz&dl=0"
            className="d-flex justify-content-center">Pull Ups: Level 7 Instruction Video</a>
        </>
      );
    }
    if (level === "8") {
      return (
        <>
          <Typography variant="body1">
            <b>Pull Ups - Level 8 (Supported Pull Ups, 10 Reps):</b> From the standing position, reach up and grab the bar with both hands. If using the TheraBand’s, step onto the band with one leg while bringing the other leg in front to secure the band in place. If a {ageGroup === "Child" ? `parent` : `partner`} is supporting manually, {genderObjectAdjective} will bend their knees so the feet are off the ground and the {ageGroup === "Child" ? `parent` : `partner`} should hold their feet and support part of the body weight. Pull body up until their chin is above the bar and then control body weight back down to the hanging position with arms straight.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_9.jpeg"
              alt="cspullups"
              width={200}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_10.jpeg"
              alt="cspullups"
              width={200}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/aid0ggrja43so0rwlp6u6/Melillo-Core-Exercises-Pull-Ups-Level-8-V2.mp4?rlkey=dlkavf5plyjv315dnb2g3s83n&st=7vzs9woy&dl=0"
            className="d-flex justify-content-center">Pull Ups: Level 8 Instruction Video</a>
        </>
      );
    }
    if (level === "9") {
      return (
        <>
          <Typography variant="body1">
            <b>Pull Ups - Level 9 (Right Angle Pull Ups, 10 Reps):</b> Starting from the hanging position, maintaining a straight line in the body, pull body up until the chin is at or above the bar, then control bodyweight back down until <b>a right angle or 90 degree angle</b> is attained in the elbow. From here pull back up until the chin is at or over the bar and repeat for <b>10 times</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_11.jpeg"
              alt="cspullups"
              width={220}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_12.jpeg"
              alt="cspullups"
              width={220}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/o8c5cjug9w7oolla5hm8g/Melillo-Core-Exercises-Pull-Ups-Level-9-V2.mp4?rlkey=t69kvlwy72wfhj1458663inew&st=k66fyp4i&dl=0"
            className="d-flex justify-content-center">Pull Ups: Level 9 Instruction Video</a>
        </>
      );
    }
    if (level === "10") {
      return (
        <>
          <Typography variant="body1">
            <b>Pull Ups - Level 10 (Pull Ups, 10):</b> Starting from the hanging position, maintaining a straight line in the body, pull body up until the chin is at or above the bar, then control the bodyweight back down to the hanging position.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_13.jpeg"
              alt="cspullups"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/pul/pul_14.jpeg"
              alt="cspullups"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/54no8o0260rocz2uo4t2p/Melillo-Core-Exercises-Pull-Ups-Level-10-V2.mp4?rlkey=37vjmckpyjlf2m9wzvj0x4ku1&st=j6srdcs1&dl=0"
            className="d-flex justify-content-center">Pull Ups: Level 10 Instruction Video</a>
        </>
      );
    }
    return null;
  }

  function LSEyelights({
    eyelight
  }) {
    if (eyelight === "Blue")
      return (
        <>
          {reportData.isReevaluation ? (
            <Typography variant="body1">
              <b>Eyelights (Blue)</b> - {firstName} is recommended to continue to use the Eyelights as part of the daily home program.  These should be worn at least <b>20 minutes, twice daily but increasing the time every 2 weeks by 10 minutes to the goal of 60 minutes twice daily</b> combined with the TENS unit and Brain driver at the same time.
            </Typography>
          ) : (
            <Typography variant="body1">
              <b>Eyelights (Blue)</b> - Blue Eyelights are used for a right brain deficit to be used as part of the daily home program.  They will be set up to blink in the left half of each eye (when looking out the glasses, the left half of each eye will be blinking on the top and bottom).  They should be worn for <b>20 minutes twice daily and increase the time every 2 weeks by 10 minutes to the maximum time of 60 minutes, twice daily</b> combined with the TENS unit and Driver.
            </Typography>
          )}

          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/eyelights_blue.jpeg"
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/toaezglkx9ldqhn3ffqu9/Melillo-Eyelights-Intructions.mp4?rlkey=qcmxw2sn1jzmihz7wjet8dtgq&st=tlg44xve&dl=0" className="d-flex justify-content-center">
            Eyelight Instruction Video
          </a>
        </>
      );

    if (eyelight === "Red")
      return (
        <>
          <Typography variant="body1">
            <b>Eyelights (Red)</b> - Red Eyelights are used for a left brain deficit to be used as part of the daily home program.  They will be set up to blink in the right half of each eye (when looking out the glasses, the right half of each eye will be blinking on the top and bottom).  They should be worn for <b>20 minutes twice daily and increase the time every 2 weeks by 10 minutes to the maximum time of 60 minutes, twice daily</b> combined with the TENS unit and Brain driver.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/eyelights_red.jpeg"
              alt="exercise"
              width={250}
              className="m-4"
            />
          </div>
          <a href="https://www.dropbox.com/scl/fi/toaezglkx9ldqhn3ffqu9/Melillo-Eyelights-Intructions.mp4?rlkey=qcmxw2sn1jzmihz7wjet8dtgq&st=tlg44xve&dl=0" className="d-flex justify-content-center">
            Eyelight Instruction Video
          </a>
        </>
      );

    return null;
  }

  function BrainBalanceMusic({
    value
  }) {
    if (value === "Brain Balance Music")
      return (
        <>
          <Typography variant="body1" className="mt-4">
            <b>Brain balance music or {program} brain music</b> -{" "}
            {ageGroup === "Child" ? "The parents" : "You"} should purchase the
            Brain Balance music for the {program} brain online at{" "}
            <a href="www.brainbalancemusic.com">www.brainbalancemusic.com</a>.{" "}
            {ageGroup === "Child" ? firstName : "You"} should listen to the music
            in just the {program === "left" ? "right" : "left"}
            ear with just 1 ear bud in or using just one headphone for <b>30 minutes
              for 1 to 2 times daily</b>.
          </Typography>
          <Typography variant="h5" className="mt-2">
            Additional {program} brain music recommendations:
          </Typography>
          <a href="https://open.spotify.com/playlist/0nWjBFv6DiCqN0mPn5VAgr?si=EaVev-aIQLux78uYJ1A-Ug&nd=1&dlsi=b358016764e14dee">
            https://open.spotify.com/playlist/0nWjBFv6DiCqN0mPn5VAgr?si=EaVev-aIQLux78uYJ1A-Ug&nd=1&dlsi=b358016764e14dee
          </a>
        </>
      );

    if (value === "Metro Timer")
      return (
        <>
          <Typography variant="body1" className="mt-2">
            <b>Metro Timer</b> - {ageGroup === "Child" ? firstName : "You"} can
            also use the Metro Timer app downloaded onto a phone also as part of
            the daily home program.{" "}
            {ageGroup === "Child" ? genderSubjectPronoun : "You"} will be using it
            at 54 BPM and hold the phone about 6 – 8 “ away from{" "}
            {ageGroup === "Child" ? genderAdjective : "your"}{" "}
            {program === "left" ? "right" : "left"} ear and allow the sound to
            play for <b>30 seconds, 1 to 2 times per day</b>.{" "}
            {ageGroup === "Child" ? genderSubjectPronoun : "You"} can also use the
            flashing light from this app, having that blink in the outside corner
            of {ageGroup === "Child" ? genderAdjective : "your"}{" "}
            {program === "left" ? "right" : "left"} eye for <b>30 seconds, 1 to 2
              times per day</b>.
          </Typography>
        </>
      );

    if (value === "Tonal Therapy") return <></>;

    return null;
  }

  function InteractiveMetronome({ level }) {

    return (
      <>
        <Typography variant="body1" component="div">
          We will be recommending that {firstName} {reportData.isReevaluation && "continues to"} use the <a href="https://d.docs.live.net/8816061ae35f8897/Documents/Melillo%20Misc/Home%20IM%20Ordering%20Information.docx">Home Interactive Metronome Unit</a> also as part of the daily home program.  This is an excellent piece of equipment that can be used throughout all phases of the home program. {genderSubjectPronoun} will be using the Melillo Method Home protocols and we can setup and monitor the progress remotely for this as the progress continues through the program.
        </Typography>
        <div className="d-flex flex-wrap justify-content-around">
          <img
            src="../../user-forms/doctor-assessment/exercise-images/interactive_metronome_1.jpg"
            alt="interactive metronome"
            width={250}
            className="m-4"
          />
          <img
            src="../../user-forms/doctor-assessment/exercise-images/interactive_metronome_2.jpg"
            alt="interactive metronome"
            width={250}
            className="m-4"
          />
        </div>
      </>
    );
  }

  function Occulomotor({ level }) {
    if (level === "1")
      return (
        <>
          <Typography variant="body1">
            <b>Level 1:</b> Will use the OKN App after downloading: At the speed of 14 and stripes or images (as explained in the instruction video above) turning the phone horizontal for left/right movement and vertical for up/down movement. {ageGroup === 'Child' ? "Parent" : "Partner"} will observe the nystagmus (beating of the eyes) if they are fixated on the images.  The goal for Level 1 is to focus on the screen long enough for this to happen consistently. For a {program} brain program the images should be moving to the {program} and {program === 'right' ? 'down' : 'up'} for <b>10 seconds</b> in each direction. OKN app for <b>10 seconds</b> in each direction. Also, use a small mirror for convergence eye exercises as explained in the video below.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_1.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_2.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_3.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_4.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/voqeutujbadwiql16zkzn/Melillo-Visual-Testing-Oculomotor-Treatment-L1-V2.mp4?rlkey=o7lj6xernub6jvctp9f0ab0bb&st=ajw4yie7&dl=0'
            className="d-flex justify-content-center">Level 1 Occulomotor Instructions</a>
        </>
      );
    if (level === "2")
      return (
        <>
          <Typography variant="body1">
            <b>Level 2:</b> Will use the OKN App after downloading: At the speed of 14 and stripes or images (as explained in the instruction video above) turning the phone horizontal for left/right movement and vertical for up/down movement. {ageGroup === 'Child' ? 'Parent' : "Partner"} will observe the nystagmus (beating of the eyes) if they are fixated on the images.
            For a {program} brain program the images should be moving to the {program} and {program === 'right' ? 'down' : 'up'} for <b>30 seconds</b> in each direction.OKN app (<b>30 seconds</b> in each direction).pencil push ups (10 reps).
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_1.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_2.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_5.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_6.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/asdw7f71t0f0xf24eqzc3/Melillo-Visual-Testing-Oculomotor-Treatment-L2-V2.mp4?rlkey=erry25tut0e6ea4211quapzr8&st=7bb78zwc&dl=0'
            className="d-flex justify-content-center">Level 2 Oculomotor Instruction Video</a>
        </>
      );
    if (level === "3")
      return (
        <>
          <Typography variant="body1">
            <b>Level 3:</b> Will use the OKN App after downloading: At the speed of 14 and stripes or images (as explained in the instruction video above) turning the phone horizontal for left/right movement and vertical for up/down movement.{ageGroup === 'Child' ? 'Parent' : "Partner"} will observe the nystagmus (beating of the eyes) if they are fixated on the images. For a {program} brain program the images should be moving to the {program} and {program === 'right' ? 'down' : 'left'} for <b>30 seconds</b> in each direction. OKN app (<b>30 seconds</b> in each direction). Also, use a string for the Brock String eye exercises as explained in the video below for 10 reps.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_1.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_2.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_7.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_8.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/gsqf6t8ik2786cb28l8qu/Melillo-Visual-Testing-Oculomotor-Treatment-L3-V2.mp4?rlkey=48gnw6sju36lvco60pcrput4p&st=la4sph19&dl=0'
            className="d-flex justify-content-center">Level 3 Occulomotor Instructions</a>
        </>
      );
    if (level === "4")
      return (
        <>
          <Typography variant="body1">
            <b>Level 4:</b> This level of the Occulomotor Activities will include a computer program at home called CogBuilder. The cogBuilder program can be purchased in our office or online: <a href="https://dr-robert-melillo.myshopify.com/products/cogbuilder-home-therapy-program">CogBuilder</a><br />
            And follow the instructions below – for Level 4, we will be using the following activities as described in the instruction video: (Tennis (level 1-2), line tracing (level 1-2), saccades (level 1-2), randot duction (1min - convergence and divergence))
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_9.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_10.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_11.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_12.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/nksr30u56x1ayw7m9z4l6/Melillo-Visual-Testing-Oculomotor-Treatment-L4-V2.mp4?rlkey=azmkudmo1trup6ea20sh55d70&st=n6fhkfia&dl=0'
            className="d-flex justify-content-center">Level 4 Occulomotor Instructions</a>
        </>
      );
    if (level === "5")
      return (
        <>
          <Typography variant="body1">
            <b>Level 5:</b> This level of the Occulomotor Activities will include a computer program at home called CogBuilder. The cogBuilder program can be purchased in our office or online: <a href="https://dr-robert-melillo.myshopify.com/products/cogbuilder-home-therapy-program">CogBuilder</a><br />
            And follow the instructions below – for Level 5, (Tennis (level 3), line tracing (level 3), saccades (level 3-4), randot (2 min - convergence and divergence), reaction time (level 1-2), visual memory (level 1-2))
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_13.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_11.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_14.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_15.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_16.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/500yo005m897jwfw6b4pg/Melillo-Visual-Testing-Oculomotor-Treatment-L5-V2.mp4?rlkey=uyfavkr0chrcw6ux6ugjurqhi&st=gva6dao5&dl=0'
            className="d-flex justify-content-center">Level 5  - Cog Builder Program for Occulomotor Activities</a>
        </>
      );
    if (level === "6")
      return (
        <>
          <Typography variant="body1">
            <b>Level 6:</b>  This level of the Occulomotor Activities will include a computer program at home called CogBuilder. The cogBuilder program can be purchased online: <a href="https://dr-robert-melillo.myshopify.com/products/cogbuilder-home-therapy-program">CogBuilder</a><br />
            And follow the instructions below – for Level 6, (Tennis (level 4), randot (3 min - convergence and divergence), line tracing (level 4), saccades (level 5-6), reaction time (level 3-4), visual memory (level 3))
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_14.jpeg"
              alt="occulomotor"
              width={220}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_17.jpeg"
              alt="occulomotor"
              width={220}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_11.jpeg"
              alt="occulomotor"
              width={220}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_15.jpeg"
              alt="occulomotor"
              width={220}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_18.jpeg"
              alt="occulomotor"
              width={220}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/8lnbu1iynt6c670w4jdwo/Melillo-Visual-Testing-Oculomotor-Treatment-L6-V2.mp4?rlkey=luxavc8t4jt6iphgtvxk9ll2u&st=ioxkxhlw&dl=0'
            className="d-flex justify-content-center">Level 6 Occulomotor Instructions</a>
        </>
      );
    if (level === "7")
      return (
        <>
          <Typography variant="body1">
            <b>Level 7:</b> Level 7 of the Occuolomotor activities will continue with the CogBuilder Home program for these activities: (Tennis (level 5), randot (4 min - convergence and divergence), line tracing (level 5), saccades (level 7), reaction time (level 5-6), visual memory (level 4))
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_19.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_14.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_20.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_11.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_15.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_21.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/1vivrffypoizo04slgi15/Melillo-Visual-Testing-Oculomotor-Treatment-L7-V2.mp4?rlkey=18kn8pkvo9z0y84trzx41x5t1&st=ssuqk69s&dl=0'
            className="d-flex justify-content-center">Level 7 Occulomotor CogBuilder Instructions</a>
        </>
      );
    if (level === "8")
      return (
        <>
          <Typography variant="body1">
            <b>Level 8:</b> Continue with the CogBuilder Program for these activities: Tennis (level 6-7), randot (5 min - convergence and divergence), saccades (level 8), reaction time (level 7), visual memory (level 5).
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_19.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_14.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_11.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_15.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_22.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/yuu4pehhzzapycf0he0yx/Melillo-Visual-Testing-Oculomotor-Treatment-L8-V2.mp4?rlkey=j2dvcb3tbjjepqji43ufz383h&st=2c74ozpy&dl=0'
            className="d-flex justify-content-center">Level 8 Occulomotor Instructions</a>
        </>
      );
    if (level === "9")
      return (
        <>
          <Typography variant="body1">
            <b>Level 9:</b> Level 9 of the Occuolomotor Activities will continue with the Cog Builder program for these activities: (Randot (5 min - convergence and divergence), reaction time (level 8), visual memory (level 6))
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_14.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_15.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_23.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/21ioine03bnbzadnfgyju/Melillo-Visual-Testing-Oculomotor-Treatment-L9-V2.mp4?rlkey=rg4lzrtpg1y391jch0603ibx1&st=d78z2xoz&dl=0'
            className="d-flex justify-content-center">Level 9 Occulomotor Instructions</a>
        </>
      );
    if (level === "10")
      return (
        <>
          <Typography variant="body1">
            <b>Level 10:</b> Continue using the CogBuilder Program for these activities: Reaction time (level 9), visual memory (level 7).
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_15.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/occ/occ_24.jpeg"
              alt="occulomotor"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/71ydw63saktzjri2bg0tr/Melillo-Visual-Testing-Oculomotor-Treatment-L10-V2.mp4?rlkey=ekr7cg2myn757h8qas5hhgz9p&st=nk55f4wn&dl=0'
            className="d-flex justify-content-center">Level 10 Occulomotor Instructions</a>
        </>
      );
    return null;
  }

  function Olfactory({ level }) {
    if (level === "1")
      return (
        <>
          <Typography variant="body1">
            <b>Level 1: No Sniff:</b> Present a smell by holding the essential oil in front of {genderAdjective} nose. At this level, we do not expect {genderObjectAdjective} to sniff and {genderSubjectPronoun} may turn away or resist in some way, such as pushing the {ageGroup === "Child" ? "parent's" : "partner's"} hand away. Place a drop of the essential oil on {genderAdjective} shirt after this exercise. Place the smell on the {program} side of {genderAdjective} shirt. Once {genderSubjectPronoun} can tolerate being exposed to a smell with no resistance <b>3 sessions in a row</b>, move to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/olf/olf_1.jpeg"
              alt="olfactory"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/7umoheloq0tawe95jxkf5/Melillo-Olfactory-Stim-Asssesment-Level-01.mp4?rlkey=uampgwe8ynjodt7lc09237lk5&st=paq65wn8&dl=0'
            className="d-flex justify-content-center">Level 1 Smell Detection Exercise Instructions</a>
        </>
      );
    if (level === "2")
      return (
        <>
          <Typography variant="body1">
            <b>Level 2: Lean in:</b> Present a smell by holding the essential oil in front of {genderAdjective} nose. Encourage {genderObjectAdjective} to sniff the smell by providing verbal cues and demonstration. At this level, {genderSubjectPronoun} may lean in but make no attempt to sniff or attempt to sniff incorrectly (e.g., blowing air out of the nose instead of sniffing in). Place a drop of the essential oil on {genderAdjective} shirt after this exercise. Place the smell on the {program} side of {genderAdjective} shirt. Once {genderSubjectPronoun} lean in and attempt to smell for <b>3 sessions in a row</b>, move to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/olf/olf_2.jpeg"
              alt="olfactory"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/olf/olf_3.jpeg"
              alt="olfactory"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/fyqq0uptftfz4gj7v42j9/Melillo-Olfactory-Stim-Asssesment-Level-02.mp4?rlkey=yi2hezq86ndv3f3bl6b5dmr09&e=1&st=dnx89ahi&dl=0'
            className="d-flex justify-content-center">Level 2 Smell Detection Exercise Instructions</a>
        </>
      );
    if (level === "3")
      return (
        <>
          <Typography variant="body1">
            <b>Level 3: Sniff without recognition:</b> Present a smell by holding the essential oil in front of {genderAdjective} nose. Encourage {genderObjectAdjective} to sniff the smell by providing verbal cues and demonstration. At this level, {genderSubjectPronoun} should be able to sniff consistently but may not be able to identify the smell. If {genderSubjectPronoun} are able to guess the smell, {genderSubjectPronoun} may do so incorrectly 75% of the time. Place a drop of the essential oil on {genderAdjective} shirt after this exercise. Place the smell on the {program} side of {genderAdjective} shirt. Once {genderSubjectPronoun} able to consistently sniff when presented with a smell for <b>3 sessions in a row</b>, move to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/olf/olf_4.jpeg"
              alt="olfactory"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/srspu6mynz5dktoeg2mdp/Melillo-Olfactory-Stim-Asssesment-Level-03.mp4?rlkey=l1nsp6wq4rx1j6u05e6dxtrqy&e=1&st=is3fr5ds&dl=0'
            className="d-flex justify-content-center">Level 3 Smell Detection Exercise Instructions</a>
        </>
      );
    if (level === "4")
      return (
        <>
          <Typography variant="body1">
            <b>Level 4: Sniff with forced choice identification:</b> Present 2 smells by holding the essential oil in front of {genderAdjective} nose. {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} smell each one and give 2 options of what the smell may be. Ask which of the 2 options the one {genderSubjectPronoun} just smelled is (e.g., "Is this lemon or mint?"). Be sure to mix up the order in which you give the options to ensure accuracy and reliability in {genderAdjective} answers as {genderSubjectPronoun} may repeat the last word {genderSubjectPronoun} hear. Place a drop of the essential oil on {genderAdjective} shirt after this exercise. Place the smell on the {program} side of {genderAdjective} shirt. Once {genderSubjectPronoun} is able to correctly identify the smell for <b>3 trials, 3 sessions in a row</b>, move to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/olf/olf_5.jpeg"
              alt="olfactory"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/shw2zjzodm6mx4u4y6ap3/Melillo-Olfactory-Stim-Asssesment-Level-04.mp4?rlkey=18ygyztrny1wci40rf6sudqks&e=1&st=pgd0wq13&dl=0'
            className="d-flex justify-content-center">Level 4 Smell Detection Exercise Instructions</a>
        </>
      );
    if (level === "5")
      return (
        <>
          <Typography variant="body1">
            <b>Level 5: Sniff and identity without forced choice (2 smells):</b> Present 2 smells by holding the essential oil in front of {genderAdjective} nose. {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} smell each one and ask to identify what {genderSubjectPronoun} smelled without giving any options as to what the smell may be. Place a drop of the essential oil on {genderAdjective} shirt after this exercise. Place the smell on the {program} side of {genderAdjective} shirt. Once {genderSubjectPronoun} is able to correctly identify <b>2 smells for 3 sessions in a row</b>, move to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/olf/olf_6.jpeg"
              alt="olfactory"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/bdrt7u2fd3yplx0kv3b08/Melillo-Olfactory-Stim-Asssesment-Level-05.mp4?rlkey=ici8xf05qm2vyzlc22klfllw5&e=1&st=mkodybq6&dl=0'
            className="d-flex justify-content-center">Level 5 Smell Detection Exercise Instructions</a>
        </>
      );
    if (level === "6")
      return (
        <>
          <Typography variant="body1">
            <b>Level 6: Sniff and identity without forced choice (3 smells):</b> Present 3 smells by holding the essential oil in front of {genderAdjective} nose. {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} smell each one and ask to identify what {genderSubjectPronoun} smelled without giving {genderObjectAdjective} any options. Place a drop of the essential oil on {genderAdjective} shirt after this exercise. Place the smell on the {program} side of {genderAdjective} shirt. Once {genderSubjectPronoun} is able to correctly identify <b>3 smells for 3 sessions in a row</b>, move to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/olf/olf_7.jpeg"
              alt="olfactory"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/vpq06wwcnn0pv15ysqfsd/Melillo-Olfactory-Stim-Asssesment-Level-06.mp4?rlkey=4i32xhhv8pm5xjzlyp35s5hhi&e=1&st=6mmucba7&dl=0'
            className="d-flex justify-content-center">Level 6 Smell Detection Exercise Instructions</a>
        </>
      );
    if (level === "7")
      return (
        <>
          <Typography variant="body1">
            <b>Level 7: Sniff and identity without forced choice (5 smells):</b> Present 5 smells by holding the essential oil in front of {genderAdjective} nose. {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} smell each one and ask {genderObjectAdjective} to identify what {genderSubjectPronoun} smelled without giving {genderObjectAdjective} any options. Place a drop of the essential oil on {genderAdjective} shirt after this exercise. Place the smell on the {program} side of {genderAdjective} shirt. Once {genderSubjectPronoun} is able to correctly identify <b>5 smells for 3 sessions in a row</b>, move to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/olf/olf_8.jpeg"
              alt="olfactory"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/iaxtdondaemnuvps4gf9r/Melillo-Olfactory-Stim-Asssesment-Level-07.mp4?rlkey=qlimgkn0rozzdcod6v2yiop22&e=1&st=ju0x6y6t&dl=0'
            className="d-flex justify-content-center">Level 7 Smell Detection Exercise Instructions</a>
        </>
      );
    if (level === "8")
      return (
        <>
          <Typography variant="body1">
            <b>Level 8: Sniff and identity smells only on weak side (5 smells):</b> Present 5 smells by holding the essential oil only on the weak side. Present the smell towards the {program} nostril. {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} smell each one and ask to identify what {genderSubjectPronoun} smelled without giving {genderObjectAdjective} any options. Place a drop of the essential oil on {genderAdjective} shirt after this exercise. Place the smell on the {program} side of {genderAdjective} shirt. Once {genderSubjectPronoun} is able to correctly identify <b>5 smells for 3 sessions in a row</b>, move to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/olf/olf_9.jpeg"
              alt="olfactory"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/n3d8fe7q21e2tys1xhutm/Melillo-Olfactory-Stim-Asssesment-Level-08.mp4?rlkey=ol1enha2hsui7b6isjqr46fy3&e=1&st=6cc9w2v3&dl=0'
            className="d-flex justify-content-center">Level 8 Smell Detection Exercise Instructions</a>
        </>
      );
    if (level === "9")
      return (
        <>
          <Typography variant="body1">
            <b>Level 9: Sniff and identity smells only on weak side (6 smells):</b> Present 6 smells by holding the essential oil only on the weak side. Present the smell towards the {program} nostril. {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} smell each one and ask to identify what {genderSubjectPronoun} smelled without giving {genderObjectAdjective} any options. Place a drop of the essential oil on {genderAdjective} shirt after this exercise. Place the smell on the {program} side of {genderAdjective} shirt. Once {genderSubjectPronoun} is able to correctly identify <b>6 smells for 3 sessions in a row</b>, move to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/olf/olf_10.jpeg"
              alt="olfactory"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/33iy63pdevnxjbxa5327k/Melillo-Olfactory-Stim-Asssesment-Level-09.mp4?rlkey=di9p818f1stynlja5ejsourlq&st=t2r2cuu9&dl=0'
            className="d-flex justify-content-center">Level 9 Smell Detection Exercise Instructions</a>
        </>
      );
    if (level === "10")
      return (
        <>
          <Typography variant="body1">
            <b>Level 10: Sniff and identity smells only on weak side (7 smells):</b> Present 7 smells by holding the essential oil only on the weak side. Present the smell towards the {program} nostril. {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} smell each one and ask to identify what {genderSubjectPronoun} smelled without giving {genderObjectAdjective} any options. Place a drop of the essential oil on {genderAdjective} shirt after this exercise. Place the smell on the {program} side of {genderAdjective} shirt. Once {genderSubjectPronoun} is able to correctly identify <b>7 smells for 3 sessions in a row</b>, move to the next level.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/olf/olf_11.jpeg"
              alt="olfactory"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/0sgbdfjfxqhasl7dlsmou/Melillo-Olfactory-Stim-Asssesment-Level-10.mp4?rlkey=u5unxckvnvdzqq02bqhupxuw5&e=1&st=y0ffxj7z&dl=0'
            className="d-flex justify-content-center">Level 10 Smell Detection Exercise Instructions</a>
        </>
      );
    return null;
  }

  function AuditoryProcessing({ level }) {
    if (program === "right") {
      if (level === "1") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 1- Brain Balance Music:</b> {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} listen to the right hemisphere music in just the left ear for <b>10-15 minutes for 1 to 2 times per day</b>. The goal for Level 1 is to {ageGroup === "Child" ? `have ${firstName}` : ``} listen to the music without taking the headphones off.<br />
              Right hemisphere music can be purchased and downloaded from <a href="https://www.brainbalancemusic.com">www.brainbalancemusic.com</a> or additional music recommendations are:<a href="https://open.spotify.com/playlist/0nWjBFv6DiCqN0mPn5VAgr?si=EaVev-aIQLux78uYJ1A-Ug&nd=1&dlsi=b358016764e14dee">Right Hemisphere Music</a>.
            </Typography>
          </>
        );
      }
      if (level === "2") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 2- Brain Balance Music:</b> {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} listen to the right hemisphere music in just the left ear for <b>15 - 20 minutes for 1 to 2 times per day</b>. {genderSubjectPronoun} can be doing other right brain stimulation or activities while listening to the music as well.<br />
              Right hemisphere music can be purchased and downloaded from <a href="https://www.brainbalancemusic.com">www.brainbalancemusic.com</a> or additional music recommendations are:<a href="https://open.spotify.com/playlist/0nWjBFv6DiCqN0mPn5VAgr?si=EaVev-aIQLux78uYJ1A-Ug&nd=1&dlsi=b358016764e14dee">Right Hemisphere Music</a>.
            </Typography>
          </>
        );
      }
      if (level === "3") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 3- Brain Balance Music:</b> {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} listen to the right hemisphere music in just the left ear for <b>20-30 minutes for 1 to 2 times per day</b>. {genderSubjectPronoun} can be doing other right brain stimulation exercises at the same time as well.<br />
              Right hemisphere music can be purchased and downloaded from <a href="https://www.brainbalancemusic.com">www.brainbalancemusic.com</a> or additional music recommendations are:<a href="https://open.spotify.com/playlist/0nWjBFv6DiCqN0mPn5VAgr?si=EaVev-aIQLux78uYJ1A-Ug&nd=1&dlsi=b358016764e14dee">Right Hemisphere Music</a>.
            </Typography>
          </>
        );
      }
      if (level === "4") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 4- Brain Balance Music:</b> {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} listen to the right hemisphere music in just the left ear for <b>30 minutes for 1 to 2 times per day</b>. {genderSubjectPronoun} can be doing other right brain stimulation exercises at the same time as well.<br />
              Right hemisphere music can be purchased and downloaded from <a href="https://www.brainbalancemusic.com">www.brainbalancemusic.com</a> or additional music recommendations are:<a href="https://open.spotify.com/playlist/0nWjBFv6DiCqN0mPn5VAgr?si=EaVev-aIQLux78uYJ1A-Ug&nd=1&dlsi=b358016764e14dee">Right Hemisphere Music</a>.
            </Typography>
          </>
        );
      }
      if (level === "5") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 5- Acoustic Pioneer:</b> At this level, we recommend the program <a href="https://www.acousticpioneer.com/">Acoustic Pioneer</a> for the home program. This can be done on a tablet or computer for very specific Auditory Processing Activities.<br />
              There are 2 games that we recommend: Insane Earplane and Zoo Caper Sky Scraper. Complete the following: Level 1-4 of Insane Earplane, Levels 1-10 of Zoo Caper.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
            </div>
          </>
        );
      }
      if (level === "6") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 6- Acoustic Pioneer:</b> At this level, we recommend the program <a href="https://www.acousticpioneer.com/">Acoustic Pioneer</a> for the home program. This can be done on a tablet or computer for very specific Auditory Processing Activities.<br />
              There are 2 games that we recommend: Insane Earplane and Zoo Caper Sky Scraper. Complete the following: Level 5-6 of Insane Earplane, Levels 11-18 of Zoo Caper
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
            </div>
          </>
        );
      }
      if (level === "7") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 7- Acoustic Pioneer:</b> Level 7 will continue with the <a href="https://www.acousticpioneer.com/">Acoustic Pioneer</a> program. Complete the following: Level 7-10 of Insane Earplane, Levels 19-26 of Zoo Caper
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
            </div>
          </>
        );
      }
      if (level === "8") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 8- Acoustic Pioneer:</b> Level 8 will continue with the <a href="https://www.acousticpioneer.com/">Acoustic Pioneer</a> program. Complete the following:  Level 11-14 of Insane Earplane, Levels 27-35 of Zoo Caper
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
            </div>
          </>
        );
      }
      if (level === "9") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 9- Acoustic Pioneer:</b> Level 9 will continue with the <a href="https://www.acousticpioneer.com/">Acoustic Pioneer</a> program. Complete the following: Level 15-17 of Insane Earplane, Levels 36-41 of Zoo Caper
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
            </div>
          </>
        );
      }
      if (level === "10") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 10- Acoustic Pioneer:</b> Level 10 will continue with the <a href="https://www.acousticpioneer.com/">Acoustic Pioneer</a> program. Complete the following: Level 18-20 of Insane Earplane, Levels 42-45 of Zoo Caper
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
            </div>
          </>
        );
      }
    }
    else if (program === 'left') {
      if (level === '1') {
        return (
          <>
            <Typography variant="body1">
              <b>Level 1- Brain Balance Music:</b> {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} listen to the left hemisphere music in just the right ear for <b>10-15 minutes for 1 to 2 times per day</b>. The goal for Level 1 is to {ageGroup === "Child" ? `Have ${firstName}` : ``} listen to the music without taking the headphones off.<br />
              Left hemisphere music can be purchased and downloaded from <a href="https://www.brainbalancemusic.com">www.brainbalancemusic.com</a> or additional music recommendations are:<a href="https://open.spotify.com/playlist/4NDLu66uYUVXx0xGXh3ooR">Left Hemisphere Music</a>.
            </Typography>
          </>
        );
      }
      if (level === '2') {
        return (
          <>
            <Typography variant="body1">
              <b>Level 2- Brain Balance Music:</b> {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} listen to the left hemisphere music in just the right ear for <b>15 - 20 minutes for 1 to 2 times per day</b>. {genderSubjectPronoun} can be doing other left brain stimulation or activities while listening to the music as well.<br />
              Left hemisphere music can be purchased and downloaded from <a href="https://www.brainbalancemusic.com">www.brainbalancemusic.com</a> or additional music recommendations are:<a href="https://open.spotify.com/playlist/4NDLu66uYUVXx0xGXh3ooR">Left Hemisphere Music</a>.
            </Typography>
          </>
        );
      }
      if (level === '3') {
        return (
          <>
            <Typography variant="body1">
              <b>Level 3- Brain Balance Music:</b> {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} listen to the left hemisphere music in just the right ear for <b>20-30 minutes for 1 to 2 times per day</b>. {genderSubjectPronoun} can be doing other left brain stimulation exercises at the same time as well.<br />
              Left hemisphere music can be purchased and downloaded from <a href="https://www.brainbalancemusic.com">www.brainbalancemusic.com</a> or additional music recommendations are:<a href="https://open.spotify.com/playlist/4NDLu66uYUVXx0xGXh3ooR">Left Hemisphere Music</a>.
            </Typography>
          </>
        );
      }
      if (level === '4') {
        return (
          <>
            <Typography variant="body1">
              <b>Level 4- Brain Balance Music:</b> {ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`} listen to the left hemisphere music in just the right ear for <b>30 minutes for 1 to 2 times per day</b>.  {genderSubjectPronoun} can be doing other left brain stimulation exercises at the same time as well.<br />
              Left hemisphere music can be purchased and downloaded from <a href="https://www.brainbalancemusic.com">www.brainbalancemusic.com</a> or additional music recommendations are:<a href="https://open.spotify.com/playlist/4NDLu66uYUVXx0xGXh3ooR">Left Hemisphere Music</a>.
            </Typography>
          </>
        );
      }
      if (level === "5") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 5- Acoustic Pioneer:</b> At this level, we recommend the program <a href="https://www.acousticpioneer.com/">Acoustic Pioneer</a> for the home program. This can be done on a tablet or computer for very specific Auditory Processing Activities.<br />
              There are 2 games that we recommend: Insane Earplane and Zoo Caper Sky Scraper. Complete the following: Level 1-4 of Insane Earplane, Levels 1-10 of Zoo Caper.
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
            </div>
          </>
        );
      }
      if (level === "6") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 6- Acoustic Pioneer:</b> At this level, we recommend the program <a href="https://www.acousticpioneer.com/">Acoustic Pioneer</a> for the home program. This can be done on a tablet or computer for very specific Auditory Processing Activities.<br />
              There are 2 games that we recommend: Insane Earplane and Zoo Caper Sky Scraper. Complete the following: Level 5-6 of Insane Earplane, Levels 11-18 of Zoo Caper
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
            </div>
          </>
        );
      }
      if (level === "7") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 7- Acoustic Pioneer:</b> Level 7 will continue with the <a href="https://www.acousticpioneer.com/">Acoustic Pioneer</a> program. Complete the following: Level 7-10 of Insane Earplane, Levels 19-26 of Zoo Caper
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
            </div>
          </>
        );
      }
      if (level === "8") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 8- Acoustic Pioneer:</b> Level 8 will continue with the <a href="https://www.acousticpioneer.com/">Acoustic Pioneer</a> program. Complete the following:  Level 11-14 of Insane Earplane, Levels 27-35 of Zoo Caper
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
            </div>
          </>
        );
      }
      if (level === "9") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 9- Acoustic Pioneer:</b> Level 9 will continue with the <a href="https://www.acousticpioneer.com/">Acoustic Pioneer</a> program. Complete the following: Level 15-17 of Insane Earplane, Levels 36-41 of Zoo Caper
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
            </div>
          </>
        );
      }
      if (level === "10") {
        return (
          <>
            <Typography variant="body1">
              <b>Level 10- Acoustic Pioneer:</b> Level 10 will continue with the <a href="https://www.acousticpioneer.com/">Acoustic Pioneer</a> program. Complete the following: Level 18-20 of Insane Earplane, Levels 42-45 of Zoo Caper
            </Typography>
            <div className="d-flex flex-wrap justify-content-around">
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg"
                alt="auditoryprocessing"
                width={220}
                className="m-4"
              />
            </div>
          </>
        );
      }
    }
  }

  function MetroTimer({ level }) {
    if (level === "1")
      return (
        <>
          <Typography variant="body1">
            <b>Level 1: MetroTimer 30 seconds, sound only:</b> For Level 1, the goal will be to simply get used to the metrotimer sound in the ear for <b>30 seconds</b> without resistance or attempting to push the phone away. The sound should be played near the {program === "left" ? "right" : "left"} ear for <b>1 - 2 times per day</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_1.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/691lr08o8y534m9xr7kue/Melillo-Visual-Testing-Metro-Timer-L1-V2.mp4?rlkey=64t9pm74w4e3yzdfgo4pborg1&st=3z9tjx9i&dl=0'
            className="d-flex justify-content-center">Level 1 Metrotimer Instructions</a>
        </>
      );
    if (level === "2")
      return (
        <>
          <Typography variant="body1">
            <b>Level 2: MetroTimer 30 seconds, sound and light:</b> At this level, the goal will be to get used to both the metrotimer sound and a flashing light for <b>30 seconds</b> without resistance or trying to push the phone away. The sound should be played near the {program === "left" ? "right" : "left"} ear with the light flashing into the {program === "left" ? "right" : "left"} corner of the {program === "left" ? "right" : "left"} eye, <b>1–2 times per day</b>.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_2.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/cy7styo5e0c5affxwah3y/Melillo-Visual-Testing-Metro-Timer-L2-V2.mp4?rlkey=ukggl0yan6znwf2h3m78p0gx2&st=wmk3nnes&dl=0'
            className="d-flex justify-content-center">Level 2 Metrotimer Instructions</a>
        </>
      );
    if (level === "3")
      return (
        <>
          <Typography variant="body1">
            <b>Level 3: Tap one leg with assistance:</b> For this exercise, the {ageGroup === "Child" ? "parent" : "partner"} will assist in tapping one hand on their leg in time with the beat for <b>30 seconds using only sound, and 30 seconds using sound and light</b>. The sound should be played near the {program === "left" ? "right" : "left"} ear with the light flashing into the {program === "left" ? "right" : "left"} corner of the {program === "left" ? "right" : "left"} eye and using the {program === "left" ? "right" : "left"} hand to tap on their {program === "left" ? "right" : "left"} leg.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <div>
              <img
                src="../../user-forms/doctor-assessment/exercise-images/mt/mt_3.jpeg"
                alt="metrotimer"
                width={250}
                className="m-4"
              />
              <img
                src="../../user-forms/doctor-assessment/exercise-images/mt/mt_4.jpeg"
                alt="metrotimer"
                width={250}
                className="m-4"
              />
            </div>
          </div>
          <a href='https://www.dropbox.com/scl/fi/f74sglc5jfnvrtwuexfln/Melillo-Visual-Testing-Metro-Timer-L3-V2.mp4?rlkey=2rk6e7ve3qnshtgwt4q2vz9qc&st=w2y9dola&dl=0'
            className="d-flex justify-content-center">Level 3 Metrotimer Instructions</a>
        </>
      );
    if (level === "4")
      return (
        <>
          <Typography variant="body1">
            <b>Level 4: Clap with assistance:</b> For this exercise, the {ageGroup === "Child" ? "parent" : "partner"} should assist in clapping both hands in time with the beat for <b>30 seconds using only sound, and 30 seconds using sound and light</b>. The sound should be played near the {program === "left" ? "right" : "left"} ear with the light flashing into the {program === "left" ? "right" : "left"} corner of the {program === "left" ? "right" : "left"} eye.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_5.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_6.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/1bukpjkoozcye6kuo011e/Melillo-Visual-Testing-Metro-Timer-L4-V2.mp4?rlkey=2m0lz7hldmbtfrxw643nhz6jw&st=o10ko4s8&dl=0'
            className="d-flex justify-content-center">Level 4 Metrotimer Instructions</a>
        </>
      );
    if (level === "5")
      return (
        <>
          <Typography variant="body1">
            <b>Level 5: Tap one hand with assistance and independently:</b> For Level 5, the {ageGroup === "Child" ? "parent" : "partner"} should assist in tapping one hand on their leg in time with the beat for half of the time and then allow them to do it independently for the other half. Do this for <b>1 minute using only sound, and 1 minute using sound and light</b>. The sound should be played near the {program === "left" ? "right" : "left"} ear with the light flashing into the {program === "left" ? "right" : "left"} corner of the {program === "left" ? "right" : "left"} eye, and using the {program === "left" ? "right" : "left"} hand to tap on their {program === "left" ? "right" : "left"} leg.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_7.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_8.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/2zfvpuz5usyhzoa9wpjr7/Melillo-Visual-Testing-Metro-Timer-L5-V2.mp4?rlkey=ytjuknrbi048bjeejot3e27yt&st=11y4evaw&dl=0'
            className="d-flex justify-content-center">Level 5 Metrotimer Instructions</a>
        </>
      );
    if (level === "6")
      return (
        <>
          <Typography variant="body1">
            <b>Level 6: Clap with assistance and independently:</b> At this level, the {ageGroup === "Child" ? "parent" : "partner"} should assist {genderObjectAdjective} in clapping both hands in time with the beat for half of the time and then allow them to do it independently for the other half. Do this for <b>1 minute using only sound and 1 minute using sound and light</b>. The sound should be played near the {program === "left" ? "right" : "left"} ear with the light flashing into the {program === "left" ? "right" : "left"} corner of the {program === "left" ? "right" : "left"} eye.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_9.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_10.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/xzfe0ncuvvldinkfq53uf/Melillo-Visual-Testing-Metro-Timer-L6-V4.mp4?rlkey=orzl0ds56bml3ltj7g61qx89i&st=3pj786ub&dl=0'
            className="d-flex justify-content-center">Level 6 Metrotimer Instructions</a>
        </>
      );
    if (level === "7")
      return (
        <>
          <Typography variant="body1">
            <b>Level 7: Tap/Clap Independently, 30 seconds:</b> For Level 7, {firstName} should be able to tap one hand on their leg and clap independently, attempting to stay on beat for <b>30 seconds with the sound and 30 seconds with sound and light</b> for each exercise. They should be accurate about 50% of the time for each exercise before moving on to the next level. The sound should be played near the {program === "left" ? "right" : "left"} ear with the light flashing into the {program === "left" ? "right" : "left"} corner of the {program === "left" ? "right" : "left"} eye and using the {program === "left" ? "right" : "left"} hand to tap on their {program === "left" ? "right" : "left"} leg.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_11.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_12.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/w81ebohy0qepnquldfqze/Melillo-Visual-Testing-Metro-Timer-L7-V3.mp4?rlkey=eqk5xxudfwv8twvrztkcb30h3&st=zu442pf1&dl=0'
            className="d-flex justify-content-center">Level 7 Metrotimer Instructions</a>
        </>
      );
    if (level === "8")
      return (
        <>
          <Typography variant="body1">
            <b>Level 8: Tap/Clap Independently, 1 minute:</b> At this level, they should be able to tap one hand on their leg and clap independently, attempting to stay on beat for <b>1 minute with the sound and 1 minute with sound and light</b> for each exercise. They should be accurate about 50% of the time for each exercise before moving on to the next level. The sound should be played near the {program === "right" ? "left" : "right"} ear with the light flashing into the {program === "right" ? "left" : "right"} corner of the {program === "right" ? "left" : "right"} eye, and using the {program === "right" ? "left" : "right"} hand to tap on their {program === "right" ? "left" : "right"} leg.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_13.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_14.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/z7xjk9861ucr8or5245sp/Melillo-Visual-Testing-Metro-Timer-L8-V2.mp4?rlkey=wzmg1ck6sl35b8vyig1ipa43j&st=qlna0yil&dl=0'
            className="d-flex justify-content-center">Level 8 Metrotimer Instructions</a>
        </>
      );
    if (level === "9")
      return (
        <>
          <Typography variant="body1">
            <b>Level 9: Tap/Clap Independently, 1 minute:</b> At this level, they should be able to tap one hand on their leg and clap independently, attempting to stay on beat for <b>1 minute with the sound and 1 minute with sound and light</b> for each exercise. They should be accurate about 75% of the time for each exercise before moving on to the next level. The sound should be played near the {program === "right" ? "left" : "right"} ear with the light flashing into the {program === "right" ? "left" : "right"} corner of the {program === "right" ? "left" : "right"} eye, and using the {program === "right" ? "left" : "right"} hand to tap on their {program === "right" ? "left" : "right"} leg.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_15.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_16.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/0b10zvjkopykp3tw6y6sy/Melillo-Visual-Testing-Metro-Timer-L9-V2.mp4?rlkey=51zf00329lc89hr6m13yuazft&st=vu5p8gww&dl=0'
            className="d-flex justify-content-center">Level 9 Metrotimer Instructions</a>
        </>
      );
    if (level === "10")
      return (
        <>
          <Typography variant="body1">
            <b>Level 10: Tap/Clap Independently, 1 minute:</b> At this level, they should be able to tap one hand on their leg and clap independently, attempting to stay on beat for <b>1 minute with the sound and 1 minute with sound and light</b> for each exercise. They should be accurate almost 100% of the time for each exercise. The sound should be played near the {program === "right" ? "left" : "right"} ear with the light flashing into the {program === "right" ? "left" : "right"} corner of the {program === "right" ? "left" : "right"} eye, and using the {program === "right" ? "left" : "right"} hand to tap on their {program === "right" ? "left" : "right"} leg.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_17.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/mt/mt_18.jpeg"
              alt="metrotimer"
              width={250}
              className="m-4"
            />
          </div>
          <a href='https://www.dropbox.com/scl/fi/adnhh01959y8nt2xk4wof/Melillo-Visual-Testing-Metro-Timer-L10-V2.mp4?rlkey=8157bvo83lfeamxb5demlz6da&st=pwhka05z&dl=0'
            className="d-flex justify-content-center">Level 10 Metrotimer Instructions</a>
        </>
      );
    return null;
  }

  function Vibration({ values }) {
    return (
      <Typography variant="body1" className="mt-2">
        {firstName} can also use a Vibration Plate at home as part of the home therapy program as a sensory modality that stimulates large nerve fibers. LifePro has a home unit available on Amazon (just as an example): <a href="https://a.co/d/eVcRxVy">LIFEPRO Vibration Unit</a>.
        This can be used at <b>{values.join(', ')}</b> speeds for <b>15 minutes</b> at a time.  This can be used while wearing sensory equipment (TENS unit, Brain Driver and Eyelights) and while doing Right/Left Brain activities at the same time.
      </Typography>
    );
  }


  function BrainDriver({ level, program }) {

    if (level === "1") {
      return (
        <>
          <Typography variant="body1">
            <b>Level 1:</b> For Level 1, {firstName} may need support from a {reportData.ageGroup === 'Child' ? 'parent' : 'partner'} to wear the Brain Driver without resisting or attempting to remove the pads. The goal will be to get them used to the Brain Driver – use the device on Broadman Area (BA) 13, 4 and 3, 1 & 2 for <b>20 minutes, twice daily</b>. This is just above and behind the ear on each side as explained in the video instructions. For a {program} brain program, the red wires will be on the {program} and black wires on the {program === 'left' ? 'right' : 'left'}. The goal will be to wear the unit, without resistance and combine while using the TENS unit and Eyelights at the same time.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_1.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_2.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_3.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_4.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/1rebzrlv4psy8njyez9vf/Melillo-Transcranial-Treatment-Level-1.mp4?rlkey=6l6yteubkopluc4511jzpmoiz&st=dm9s0tad&dl=0"
            className="d-flex justify-content-center mt-2">Level 1 Brain driver Instructions</a>
        </>
      );
    }
    else if (level === "2") {
      return (
        <>
          <Typography variant="body1">
            <b>Level 2:</b> This level of the Brain Driver will be recommended to use 2 units at once.  If only using 1 unit, then alternate locations between the front (BA 39,40) for once a day and the back (BA 13) once a day so you are doing both areas.  Use the Brain Driver on Broadman Area (BA) 39,40 and BA 13 for <b>20 minutes, preferably twice daily</b>.  Every 2 weeks, try to increase the time up by 10 more minutes for the goal of <b>60 minutes, twice daily</b> and strength will be based upon tolerance. The are is just above and behind the ear and then in between the midline of the face and hairline above the eyebrow as explained in the video instructions.  For a {program} brain program, the red wires will always be on the {program} and black wires on the {program === 'left' ? 'right' : 'left'}. Combine the Brain Driver with the TENS unit and Eyelights.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_6.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/mpeaz7rva8df8okrkfobp/Melillo-Transcranial-Level-2-V4.mp4?rlkey=wdl76ludaml6mjr2xtahqas0t&e=1&st=iqem4o2s&dl=0"
            className="d-flex justify-content-center mt-2">Level 2 Brain driver Instructions</a>
        </>
      );
    }
    else if (level === "3") {
      return (
        <>
          <Typography variant="body1">
            <b>Level 3:</b> For Level 3, {firstName}  will be recommended to use 2 Brain Drivers at once or use the 1 Brain Driver doing 1 area (BA 13) in the morning and the other area(BA 44, 45, 47) in the evening.  Using the Brain Driver on Broadman Areas (BA) 13 (just above the ear on each side) and BA 44, 45 & 47 (half way on the hairline in the front and half on the forehead) for <b>20 minutes each</b> and increasing time every <b>2 weeks by 10 minutes</b> if possible and use with the other sensory equipment (like the TENS unit and Eyelights) for the goal amount of <b>60 minutes, twice daily</b>. For a {program} brain program, the red wires will always be on the {program} and black wires on the {program === 'left' ? 'right' : 'left'}.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_7.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/ya6ginpdqjb3py3okhgh5/Melillo-Transcranial-Treatment-Level-3.mp4?rlkey=xpjhi49haelzi8eefpfcvlghu&st=qmhlva94&dl=0"
            className="d-flex justify-content-center mt-2">Level 3 Brain driver Instructions</a>
        </>
      );
    }
    else if (level === "4") {
      return (
        <>
          <Typography variant="body1">
            <b>Level 4:</b> For Level 4, it is recommended to use 2 Brain Drivers at once, or if using just 1 unit,   alternate front (BA 13) once a day and back area (BA 6, 8) once a day.  The Brain Driver will be used on Broadman Areas (BA) 13 and BA 6, 8 while using the sensory equipment (TENS unit and Eyelights) at the same time starting with <b>20 minutes</b> and increasing time every <b>2 weeks by 10 more minutes</b> to the goal of <b>60 minutes, twice daily</b> and to the strength that is able to be tolerated.  For a {program} brain program, the red wires will be on the {program} side and black wires on the {program === 'left' ? 'right' : 'left'} side.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_8.jpeg"
              alt="braindriver"
              width={220}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg"
              alt="braindriver"
              width={220}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/pdvcuh6800hd7h1y2pd05/Melillo-Transcranial-Treatment-Level-4.mp4?rlkey=po3tjvmj7xtyrvfr526pfvg8y&st=2vtdq0bs&dl=0"
            className="d-flex justify-content-center mt-2">Level 4 Brain driver Instructions</a>
        </>
      );
    }
    else if (level === "5") {
      return (
        <>
          <Typography variant="body1">
            <b>Level 5:</b> Use 2 Brain Drivers at once or if only using 1 unit, alternate as 1 area in the morning and 1 in the evening.  Use the brain driver on Broadman areas (BA) 6,8 and BA 44, 45, 47 (on the vertical hairline above the eyebrow). For a {program} brain program, the red wires will be on the {program} side and black wires on the {program === 'left' ? 'right' : 'left'} side. Starting with <b>20 minutes and increasing time by 10 minutes every 2 weeks to goal of 60 minutes, twice daily</b> with the TENS unit and Eyelights at the same time.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_9.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/fpye5sngi8uydetbibgs8/Melillo-Transcranial-Treatment-Level-5.mp4?rlkey=25873u1byqzwwrnmiey7uei5f&st=pggtesln&dl=0"
            className="d-flex justify-content-center mt-2">Level 5 Brain driver Instructions</a>
        </>
      );
    }
    else if (level === "6") {
      return (
        <>
          <Typography variant="body1">
            <b>Level 6:</b> Use 2 Brain Drivers at once or if only using 1 unit, alternate 1 area in the morning and 1 in the evening.  Use the brain driver on Broadman areas (BA) 9, 46 and BA 10, 11, 12(halfway between the midline of the face and the vertical hairline) as shown in the video below. For a {program} brain program, the red wires will be on the {program} side and black wires on the {program === 'left' ? 'right' : 'left'} side.  Starting with <b>20 minutes and increasing time by 10 minutes every 2 weeks to goal of 60 minutes, twice daily</b> with the TENS unit and Eyelights at the same time.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_10.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/aiptw6j2eej0w7coedj3g/Melillo-Transcranial-Treatment-Level-6.mp4?rlkey=3iz0qdzpv5gbhhps727gnlkiu&st=ktu8yv0z&dl=0"
            className="d-flex justify-content-center mt-2">Level 6 Brain driver Instructions</a>
        </>
      );
    }
    else if (level === "7") {
      return (
        <>
          <Typography variant="body1">
            <b>Level 7:</b> Use 2 Brain Drivers at once or if only using 1 unit, alternate 1 area in the morning and 1 in the evening.  Use the brain driver on Broadman areas (BA) 5, 7 and BA 10, 11, 12 (Just above the eyebrow, in between the midline of the face and the vertical hairline) as shown in the video below. For a {program} brain program, the red wires will be on the {program} side and black wires on the {program === 'left' ? 'right' : 'left'} side.  Starting with <b>20 minutes and increasing time by 10 minutes every 2 weeks to goal of 60 minutes, twice daily</b> with the TENS unit and Eyelights at the same time.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_11.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/acpze521dbxoi3ylz102k/Melillo-Transcranial-Treatment-Level-7.mp4?rlkey=uqi8a8ej28cihs2cbnfzrf92j&st=dj7svpp9&dl=0"
            className="d-flex justify-content-center mt-2">Level 7 Brain driver Instructions</a>
        </>
      );
    }
    else if (level === "8") {
      return (
        <>
          <Typography variant="body1">
            <b>Level 8:</b> Use 2 Brain Drivers at once or if only using 1 unit, alternate 1 area in the morning and 1 in the evening.  Use the brain driver on Broadman areas (BA) 39, 40 (1” above the ear and 1” behind the ear) and BA 44, 45, 47 (in the middle of the vertical hairline) as shown in the video below. For a {program} brain program, the red wires will be on the {program} side and black wires on the {program === 'left' ? 'right' : 'left'} side. Starting with <b>20 minutes and increasing time by 10 minutes every 2 weeks to goal of 60 minutes, twice daily</b> with the TENS unit and Eyelights at the same time.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_12.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/ct7xkfduvgza0na6fkxrg/Melillo-Transcranial-Level-8.mp4?rlkey=c7x7vtmccoewdodi19v57sdnm&st=o7itfqny&dl=0"
            className="d-flex justify-content-center mt-2">Level 8 Brain driver Instructions</a>
        </>
      );
    }
    else if (level === "9") {
      return (
        <>
          <Typography variant="body1">
            <b>Level 9:</b> Use 2 Brain Drivers at once or if only using 1 unit, alternate 1 area in the morning and 1 in the evening.  Use the brain driver on Broadman areas (BA) 39, 40 (1” above the ear and 1” behind the ear) and BA 10, 11, 12 (Just above the eyebrow, in between the midline of the face and the vertical hairline) as shown in the video below. For a {program} brain program, the red wires will be on the {program} side and black wires on the {program === 'left' ? 'right' : 'left'} side.  Starting with <b>20 minutes and increasing time by 10 minutes every 2 weeks to goal of 60 minutes, twice daily</b> with the TENS unit and Eyelights at the same time.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_13.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/muy06cjndbmx9er5e7wkj/Melillo-Transcranial-Treatment-Level-9.mp4?rlkey=ckqc3uxe12k8ozj9tb8zv3y4g&st=hjxxixl1&dl=0"
            className="d-flex justify-content-center mt-2">Level 9 Brain driver Instructions</a>
        </>
      );
    }
    else if (level === "10") {
      return (
        <>
          <Typography variant="body1">
            <b>Level 10:</b> Use 2 Brain Drivers at once or if only using 1 unit, alternate 1 area in the morning and 1 in the evening.  Use the brain driver on Broadman areas (BA) 39, 40 (1” above the ear and 1” behind the ear) and BA 9, 46  as shown in the video below. For a {program} brain program, the red wires will be on the {program} side and black wires on the {program === 'left' ? 'right' : 'left'} side. Starting with <b>20 minutes and increasing time by 10 minutes every 2 weeks to goal of 60 minutes, twice daily</b> with the TENS unit and Eyelights at the same time.
          </Typography>
          <div className="d-flex flex-wrap justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/bd/bd_14.jpeg"
              alt="braindriver"
              width={250}
              className="m-4"
            />
          </div>
          <a
            href="https://www.dropbox.com/scl/fi/7fd2ta2xvl7lmgu0t37tp/Melillo-Transcranial-Treatment-Level-10.mp4?rlkey=nb3s8wfew9n6rlsvq6fvz0z9b&st=7ske17er&dl=0"
            className="d-flex justify-content-center mt-2">Level 10 Brain driver Instructions</a>
        </>
      );
    }
    else {
      return (
        <Typography variant="body1">
          No data available for this level.
        </Typography>
      );
    }
  }

  function Vitamin({ vitamin }) {
    const isReevaluation = reportData.isReevaluation;
    const genderSubject = reportData.gender === "Male" ? "he" : "she";
    const genderObject = reportData.gender === "Male" ? "him" : "her";

    if (vitamin.includes("Kid Genius") && vitamin.includes("Under 5")) {
      if (vitamin.includes("Left"))
        return (
          <Typography variant="body1" className="mt-2">
            {isReevaluation ? `Recommendation for ${genderObject} to continue the ` : "Recommendation for the "}
            <a href="https://www.dropbox.com/scl/fi/0h272l49b0lls57xday07/learningmemory-concentration-vits.mov?rlkey=yxmtiwo6jtz7mmk55hrta4n7u&e=1&dl=0">
              Kid Genius Left Brain (Learning and Memory)
            </a> supplement. This will be 1 capsule, twice daily, preferably with food. The left-brain vitamins provide neurotransmitters for left-brain development.

          </Typography>
        );
      if (vitamin.includes("Right"))
        return (
          <Typography variant="body1" className="mt-2">
            {isReevaluation ? `Recommendation for ${genderObject} to continue the ` : "Recommendation for the "}
            <a href="https://www.dropbox.com/scl/fi/ma8f3qz1paqb8c15yopp6/Attention-Focus-Calm.mp4?rlkey=d2x8ni5csmp7u07p2jwyhq2vq&st=904pecjg&dl=0">
              Kid Genius Right Brain (Attention & Focus)
            </a> supplement. This will be 1 capsule, twice daily, preferably. We also have a new chewable version for kids as well. The right-brain vitamins provide neurotransmitters for right-brain development which is very important.
          </Typography>
        );
      if (vitamin.includes("Probiotic"))
        return (
          <Typography variant="body1" className="mt-2">
            {isReevaluation ? `Also recommending that ${genderSubject} continues to take the ` : "Also recommending the "}
            <a href="https://www.dropbox.com/scl/fi/a0fdbjrsf09a7zyk12xba/Gut-Vitamin.mp4?rlkey=9zpaiu47sw0sjc2jxh972vvk4&st=c0eor913&dl=0">
              Kid Genius Prebiotic/Probiotic
            </a> supplement for 1 capsule, twice daily, with food. These digestion vitamins help to provide important digestive enzymes to the body which are essential for proper digestion while we are working with the program.
          </Typography>
        );
    }
    if (vitamin.includes("Kid Genius") && vitamin.includes("Over 5")) {
      if (vitamin.includes("Left"))
        return (
          <Typography variant="body1" className="mt-2">
            {isReevaluation ? `Recommendation for ${genderObject} to continue the ` : "Recommendation for the "}
            <a href="https://www.dropbox.com/scl/fi/0h272l49b0lls57xday07/learningmemory-concentration-vits.mov?rlkey=yxmtiwo6jtz7mmk55hrta4n7u&e=1&dl=0">
              Kid Genius Left Brain (Learning and Memory)
            </a> supplement. This will be 2 capsules, twice daily, preferably with food. The left-brain vitamins provide neurotransmitters for left-brain development.
          </Typography>
        );
      if (vitamin.includes("Right"))
        return (
          <Typography variant="body1" className="mt-2">
            {isReevaluation ? `Recommendation for ${genderObject} to continue the ` : "Recommendation for the "}
            <a href="https://www.dropbox.com/scl/fi/ma8f3qz1paqb8c15yopp6/Attention-Focus-Calm.mp4?rlkey=d2x8ni5csmp7u07p2jwyhq2vq&st=904pecjg&dl=0">
              Kid Genius Right Brain (Attention & Focus)
            </a> supplement. This will be 2 capsules, twice daily, preferably. We also have a new chewable version for kids as well. The right-brain vitamins provide neurotransmitters for right-brain development which is very important.
          </Typography>
        );
      if (vitamin.includes("Probiotic"))
        return (
          <Typography variant="body1" className="mt-2">
            {isReevaluation ? `Also recommending that ${genderSubject} continues to take the ` : "Also recommending the "}
            <a href="https://www.dropbox.com/scl/fi/a0fdbjrsf09a7zyk12xba/Gut-Vitamin.mp4?rlkey=9zpaiu47sw0sjc2jxh972vvk4&st=c0eor913&dl=0">
              Kid Genius Prebiotic/Probiotic
            </a>supplement for 2 capsules, twice daily, with food. These digestion vitamins help to provide important digestive enzymes to the body which are essential for proper digestion while we are working with the program.
          </Typography>
        );
    }

    if (vitamin.includes("Vitamin D capsules"))
      return (
        <Typography variant="body1" className="mt-2">
          <a href="https://www.dropbox.com/scl/fi/kmry9tlraa8smeul33zom/vitamin-Ds.mov?rlkey=vaalvez7ap8yj3uoad8cdcu6y&dl=0">
            Vitamin D capsules
          </a> {vitamin === "Vitamin D capsules - Under 5" ? "2,500" : "5,000"} IU Capsules or gummies are also recommended{isReevaluation ? " to be continued daily" : ""}. Vitamin D helps to balance the immune system which will be important while the brain is developing through this stage. We also have a liquid version available.
        </Typography>
      );
    if (vitamin.includes("Vitamin D liquid"))
      return (
        <Typography variant="body1" className="mt-2">
          <a href="https://www.dropbox.com/scl/fi/kmry9tlraa8smeul33zom/vitamin-Ds.mov?rlkey=vaalvez7ap8yj3uoad8cdcu6y&e=1&dl=0">
            Vitamin D liquid
          </a> is also recommended{isReevaluation ? " to be continued daily " : " "}at this stage to be used daily. The liquid will be {vitamin === "Vitamin D liquid - Under 5" ? "1.5" : "2.5"} drops for {vitamin === "Vitamin D liquid - Under 5" ? "2,500" : "5,000"} units per day. Vitamin D helps to balance the immune system which is important while the brain is developing through this stage.
        </Typography>
      );
    if (vitamin === "Omega 3 capsules - Over 5")
      return (
        <Typography variant="body1" className="mt-2">
          <a href="https://www.dropbox.com/scl/fi/2ty1lxjb6emc9tg6p6qqn/omegas-vits.mov?rlkey=gk7wc12l3aep22k8t5qqsoj0s&e=1&dl=0">
            Omega 3
          </a> soft gel capsules are also an essential recommendation that should {isReevaluation ? "continue to " : ""}be taken daily for 5,000 mg. Omega is very important for brain function, brain development and reducing inflammation and in controlling and developing the immune system
        </Typography>
      );
    if (vitamin.includes("Omega 3 liquid"))
      return (
        <Typography variant="body1" className="mt-2">
          <a href="https://www.dropbox.com/scl/fi/2ty1lxjb6emc9tg6p6qqn/omegas-vits.mov?rlkey=gk7wc12l3aep22k8t5qqsoj0s&e=1&dl=0">
            Omega 3 liquid
          </a> is also an essential recommendation that should {isReevaluation ? "continue to " : ""}be taken daily for {vitamin === "Omega 3 liquid - Under 5" ? "2,500" : "5,000"} mg. Omega is very important for brain function, brain development and reducing inflammation and in controlling and developing the immune system.
        </Typography>
      );
    if (vitamin.includes("TH1/TH2"))
      return (
        <Typography variant="body1" className="mt-2">
          The TH1/TH2 supplements are also recommended to be {isReevaluation ? "continued " : ""}taken at home.{" "}
          <a href="https://www.dropbox.com/scl/fi/yjlxy5uqbtxazc638lgdt/TH1-Support.mp4?rlkey=52hljm7i054ctyf5g6vwy8bgz&st=89h8ztnj&dl=0">
            TH1 Supplement Video
          </a>{" "}
          and{" "}
          <a href="https://www.dropbox.com/scl/fi/vdfe95rb5xqvkogsu91b1/TH2-Modulator.mp4?rlkey=ebqvodh0yzntmecrl97cwprsg&st=s8xzdlbu&dl=0">
            TH2 Supplement Video
          </a>{" "}
          explain how to use these properly. These are used to build the immune system, fight infections and create antibodies. They can be purchased in our office or online at <a href="www.pureforyou.com">www.pureforyou.com</a>. They will be taken for {vitamin === "TH1/TH2 - Under 5" ? "1" : "2"} capsule of each, twice daily.
        </Typography>
      );
    if (vitamin.includes("Zeolite/Glutathione Spray"))
      return (
        <>
          <Typography variant="body1" className="mt-2">
            The <a href="https://www.dropbox.com/scl/fi/en6fx3r9ec6oflgl4x8yh/Glutathione.mp4?rlkey=3ciqv7o68o2km976ardbrcymo&st=it4pp6se&dl=0">
              Glutathione
            </a> and <a href="https://www.dropbox.com/scl/fi/gfluijr3klow37szt0vqz/Zeolite-Spray.mp4?rlkey=e46d8gqj3mz4circaizwpxns9&st=9g45eze7&dl=0">
              Zeolite Spray
            </a> is also recommended to be {isReevaluation ? "continued " : ""}used daily at home while doing the program. Glutathione is an all-natural chemical that is made in the body and used for detoxification and Zeolite helps to excrete the toxins. The sprays are odorless and tasteless and just absorb in the mouth very easily. They can be purchased in our office, online or on amazon and will be used for {vitamin === "Zeolite/Glutathione Spray - Under 5" ? "3" : "6"} sprays of each, twice daily.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/glutathione_spray.jpeg"
              alt="vitamin"
              width={120}
              className="m-4"
            />
          </div>
        </>
      );

    if (vitamin.includes("B12 Spray"))
      return (
        <>
          <Typography variant="body1" className="mt-2">
            <a href="https://www.dropbox.com/scl/fi/salnvf0hoi5xfes2qpnv0/B12.mp4?rlkey=kgg96u7240qstthpzuzrtpux8&st=3lwoxvii&dl=0">
              B12 Spray
            </a>{" "}
            is also an essential part of the home recommendations{isReevaluation ? " to be continued daily" : ""}. This spray is odorless and tasteless and will be sprayed directly into the mouth for the body to absorb easily. B12 is essential to promote healthy brain function and development. This spray can be purchased in our office, on our website or also on amazon and will be used 6 sprays, twice daily, every day as part of the home program.
          </Typography>
          <div className="d-flex justify-content-around">
            <img
              src="../../user-forms/doctor-assessment/exercise-images/vitamin_b12.jpeg"
              alt="vitamin"
              width={120}
              className="m-4"
            />
          </div>
        </>
      );

    if (vitamin.includes("Perilla"))
      return (
        <>
          <Typography variant="body1" className="mt-2">
            <a href="https://a.co/d/aKE74eD">
              Perilla
            </a> supplement, made by Pure Encapsulations, is also being recommended{isReevaluation ? " to be continued" : ""}. This will help to improve the immune response and Th2 and maintain a healthy modulation of the immune system to environmental factors. This can be purchased in our office or on Amazon and will be taken for {vitamin === "Perilla - Under 5" ? "1" : "2"} capsule, twice daily.
          </Typography>
        </>
      );

    if (vitamin.includes("Histox"))
      return (
        <>
          <Typography variant="body1" className="mt-2">
            <a href="https://a.co/d/gR6eLrm">
              Histox
            </a> by Apex Energetic is also recommended{isReevaluation ? " to be continued" : ""} for {vitamin === "Histox - Under 5" ? "1" : "2"} capsule, twice daily. This supplement will aid in the body’s response to foods and other environmental factors and support immune function. It will also aid in gastrointestinal function and provide antioxidants. This can be purchased in our office on also on Amazon.
          </Typography>
        </>
      );

    if (vitamin.includes("Quercetin"))
      return (
        <>
          <Typography variant="body1" className="mt-2">
            <a href="https://www.dropbox.com/scl/fi/gxej2twavzbyfx14deod4/Quercetin-Spray.mp4?rlkey=t1qyqe09du2yqsw5staliggsd&st=rjlew04v&dl=0">
              Quercetin
            </a> is also recommended{isReevaluation ? " to be continued" : ""} for {vitamin === "Quercetin - Under 5" ? "3" : "6"} sprays, twice daily. This can be purchased in our office or on our website too.
          </Typography>
        </>
      )

    if (vitamin.includes("Berberine"))
      return (
        <>
          <Typography variant="body1" className="mt-2">
            <a href="https://www.dropbox.com/scl/fi/4mysvot0txwnis93qruiw/Berberine.mp4?rlkey=1fei04asyzkv44hxh2ig59ea9&st=zw7qxj66&dl=0">
              Berberine
            </a> is also recommended{isReevaluation ? " to be continued" : ""} for {vitamin === "Berberine - Under 5" ? "3" : "6"} sprays, twice daily. This can be purchased in our office or on our website too.
          </Typography>
        </>
      )

    if (vitamin.includes("COQ10"))
      return (
        <>
          <Typography variant="body1" className="mt-2">
            <a href="https://www.dropbox.com/scl/fi/1q9gkux135bgq4ii9wj5g/Co-Q10.mp4?rlkey=px1cl73qahlipw6pbec0q0lm8&st=fo4vhj6v&dl=0">
              COQ10
            </a> is also recommended{isReevaluation ? " to be continued" : ""} for {vitamin === "COQ10 - Under 5" ? "3" : "6"} sprays, twice daily. This can be purchased in our office or on our website too.
          </Typography>
        </>
      )

    if (vitamin.includes("Pro Omega 2000") && vitamin.includes("Over 5"))
      return (
        <>
          <Typography variant="body1" className="mt-2">
            <a href="https://www.dropbox.com/scl/fi/7p17rhp0b8ca6gvlnk403/ProOmega-2000.mp4?rlkey=9t4kw3yu1cx2a9o6qcwa8p7zn&e=1&st=p7aupx6z&dl=0">
              Pro Omega 2000
            </a> is also recommended{isReevaluation ? " to be continued" : ""} for 5 capsules daily. Follow the recommended instructions and this can be purchased in our office or Amazon.
          </Typography>
        </>
      )
    return null;
  }

  function VibrantTesting({ value }) {
    if (value === "Food Sensitivity")
      return (
        <Typography variant="body1" className="mt-2">
          I have also discussed a Food Sensitivity Lab test for {firstName}. This
          is an at home finger-prick test that can help to evaluate any
          sensitivity that {genderSubjectPronoun} has to specific foods or additives and how those
          may affect {genderAdjective} neurologically. We will do a separate zoom call with{" "}
          {ageGroup === "Child" ? "the family" : "you"} to review these results
          when they are ready. This can be ordered in our office or online at:{" "}
          <a href="https://drrobertmelillo.wellproz.com/patient/home/">
            Well ProZ
          </a>
        </Typography>
      );
    if (value === "Neural Zoomer Plus")
      return (
        <Typography variant="body1" className="mt-2">
          I have also discussed a Neural Zoomer Lab test for {firstName}. This is
          an at home finger-prick test that can help to evaluate {genderAdjective} immune
          system, and any inflammation in {genderAdjective} body that may affect {genderAdjective}
          neurologically. We will do a separate zoom call with{" "}
          {ageGroup === "Child" ? "the family" : "you"} to review these results
          when they are ready. This can be ordered in our office or online at:{" "}
          <a href="https://drrobertmelillo.wellproz.com/patient/home/">
            Well ProZ
          </a>
        </Typography>
      );
    return null;
  }

};

export default StimFormReport;